import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import './CheckoutTheme.css';
import { getWebsiteThemeAttribution } from '../../experiments/websiteTheme';
import { Container, Row, Col, Overlay, Tooltip, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import CheckoutStripeElements from '../../components/CheckoutStripeElements';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { FaShieldAlt, FaCheckCircle, FaTag, FaTimes, FaCheck } from 'react-icons/fa';
import useUser from '../../hooks/useUser';
import useABTest from '../../hooks/useABTest';
import useCheckoutExperienceExperiment from '../../hooks/useCheckoutExperienceExperiment';
import HostedCheckoutLauncher from '../../components/HostedCheckout/HostedCheckoutLauncher';
import TrustStrip from '../../components/TrustStrip';
import StripeErrorBoundary from '../../components/StripeErrorBoundary';
import { confirmSetupWithClient, confirmPaymentWithClient } from '../../utils/stripeClient';
import {
  CheckoutContactFields,
  CheckoutTrialCallout,
  CheckoutPaymentContainer,
  HowFoundUsDropdown,
} from '../../components/CheckoutFormShared';
import { buildSmsConsentPayload } from '../../components/SmsConsent';
import {
  pushEvent,
  ANALYTICS_EVENTS,
  generateCheckoutTransactionId,
  identifyUserFromLeadCapture,
  trackBeginCheckout,
} from '../../utils/analytics';
import { checkoutAnalyticsValue } from '../../utils/checkoutAnalyticsValue';
import {
  getAffiliateCodeForApi,
  getUtmParamsForApi,
  getAttributionParams,
} from '../../utils/utmTracker';
import { loadPromptFromSession } from '../../utils/aiPromptStorage';
import { usePaymentStatus } from '../../contexts/PaymentStatusContext';
import { convertLeadToTrial } from '../../utils/stripeConversion';
import { ACTIVE_EXPERIMENTS } from '../../utils/experiments';
import { getAgentHandoff } from '../../utils/aiAgentHandoff';
import { validatePromoCode } from '../../api/checkout';
import { buildCouponDescription } from '../../utils/couponDescription';
import fetchWithTimeout from '../../utils/fetchWithTimeout';
import { createCheckoutAttemptId } from '../../utils/checkoutAttemptId';
import { classifyCheckoutResponse } from '../../utils/checkoutResponse';
import { completeStripeAutoLogin } from '../../utils/stripeAutoLogin';
import { applyThemeMode, useThemeMode } from '../../design-system';

import { isPreviewExperimentOverrideHost } from '../../utils/experimentOverrideHosts';
import {
  buildPostPurchaseChallengePath,
  sanitizeInternalDestination,
} from '../../utils/postSignupJourney';

import './StarterCheckoutPage.css';

const stripeKey = process.env.REACT_APP_STRIPE_PK || '';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082';
const PAYMENT_INTENT_FETCH_TIMEOUT_MS = 45000;
const AUTO_LOGIN_FETCH_TIMEOUT_MS = 10000;
const SHOULD_LOG_CHECKOUT_DEBUG = process.env.NODE_ENV !== 'production';

const TERMS_DOC_TYPE = 'main_tos';
const SALES_PAGE_EXPERIMENT_PARAM_KEYS = [
  '$feature/sales_page_signup_framing_v4_variant',
  '$feature_flag_sales_page_signup_framing_v4_variant',
  '$feature/sales_page_signup_framing_v5_variant',
  '$feature_flag_sales_page_signup_framing_v5_variant',
  '$feature/google_search_signup_experience_v1_variant',
  '$feature_flag_google_search_signup_experience_v1_variant',
  '$feature/google_search_signup_experience_v2_variant',
  '$feature_flag_google_search_signup_experience_v2_variant',
  '$feature/google_search_auth_first_personalization_2026_08_variant',
  '$feature_flag_google_search_auth_first_personalization_2026_08_variant',
  'feature_flag',
  'experiment_name',
  'experiment_variant',
  'sales_page_signup_framing_v4_experiment_name',
  'sales_page_signup_framing_v5_experiment_name',
  'sales_page_signup_framing_variant',
  'sales_page_signup_framing_v4_variant',
  'sales_page_signup_framing_v4_route_variant',
  'sales_page_signup_framing_v5_variant',
  'sales_page_signup_framing_v5_route_variant',
  'google_search_signup_experience_v1_experiment_name',
  'google_search_signup_experience_v1_variant',
  'google_search_signup_experience_v2_experiment_name',
  'google_search_signup_experience_v2_variant',
  'google_search_auth_first_personalization_2026_08_experiment_name',
  'google_search_auth_first_personalization_2026_08_variant',
  'google_search_lp_job',
  'sales_page_form_variant',
  'sales_page_framing_variant',
  'sales_page_design_variant',
];
const SALES_SIGNUP_V4_FEATURE_FLAG = 'sales_page_signup_framing_v4_variant';
const SALES_SIGNUP_V4_EXPERIMENT_NAME = 'sales_page_signup_framing_v4';
const SALES_SIGNUP_V4_ROUTE_VARIANTS = ['full_form', 'email_only', 'auth_first'];
const SALES_SIGNUP_V4_POSTHOG_VARIANTS = ['control', 'email_only', 'auth_first'];
const SALES_SIGNUP_V5_FEATURE_FLAG = 'sales_page_signup_framing_v5_variant';
const SALES_SIGNUP_V5_EXPERIMENT_NAME = 'sales_page_signup_framing_v5';
const SALES_SIGNUP_V5_ROUTE_VARIANTS = ['email_only', 'auth_first'];
const SALES_SIGNUP_V5_POSTHOG_VARIANTS = ['control', 'auth_first'];
const PRICE_EXPERIMENT_VARIANTS = ['control', 'test'];
const CHECKOUT_THEME_VARIANTS = ['light', 'dark'];

const EMPTY_SAVED_PAYMENT_METHODS = Object.freeze({
  paymentMethods: [],
  loading: false,
  error: null,
  pending: false,
  hasMethods: false,
});

const BANK_VERIFICATION_METHOD = 'automatic';

const dollarsToCents = (amount, fallback = 0) => {
  const parsedAmount = Number(amount);
  if (!Number.isFinite(parsedAmount)) {
    return fallback;
  }
  return Math.max(0, Math.round(parsedAmount * 100));
};

const centsToUsd = (amountCents) => {
  const parsedAmount = Number(amountCents);
  if (!Number.isFinite(parsedAmount)) {
    return 0;
  }
  return Math.max(0, parsedAmount) / 100;
};

const TERMS_ACCEPTANCE_SHORT_MESSAGE = 'Please accept the Terms of Service to continue';
const TERMS_ACCEPTANCE_ERROR_MESSAGE =
  'Please accept the Terms of Service to continue with your subscription.';

const isTermsAcceptanceErrorMessage = (message) =>
  message === TERMS_ACCEPTANCE_SHORT_MESSAGE || message === TERMS_ACCEPTANCE_ERROR_MESSAGE;

const normalizeSalesSignupV4RouteVariant = (value) => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized =
    value === 'control' ? 'full_form' : value === 'login_menu' ? 'auth_first' : value;
  return SALES_SIGNUP_V4_ROUTE_VARIANTS.includes(normalized) ? normalized : null;
};

const normalizeSalesSignupV4PostHogVariant = (value) => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized =
    value === 'full_form' ? 'control' : value === 'login_menu' ? 'auth_first' : value;
  return SALES_SIGNUP_V4_POSTHOG_VARIANTS.includes(normalized) ? normalized : null;
};

const normalizeSalesSignupV5RouteVariant = (value) => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized =
    value === 'control' ? 'email_only' : value === 'login_menu' ? 'auth_first' : value;
  return SALES_SIGNUP_V5_ROUTE_VARIANTS.includes(normalized) ? normalized : null;
};

const normalizeSalesSignupV5PostHogVariant = (value) => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized =
    value === 'email_only' ? 'control' : value === 'login_menu' ? 'auth_first' : value;
  return SALES_SIGNUP_V5_POSTHOG_VARIANTS.includes(normalized) ? normalized : null;
};

const getSalesSignupV4FormVariant = (routeVariant) =>
  routeVariant === 'email_only' ? 'email_only' : 'full_form';

const getSalesSignupV4FramingVariant = (routeVariant) =>
  routeVariant === 'auth_first' ? 'auth_first' : 'create_account';

const getSalesSignupV4DesignVariant = (routeVariant) =>
  routeVariant === 'auth_first' ? 'login_menu' : 'sales07';

export const buildSalesSignupAttribution = (params = {}) => {
  const isV5 =
    params.feature_flag === SALES_SIGNUP_V5_FEATURE_FLAG ||
    params.experiment_name === SALES_SIGNUP_V5_EXPERIMENT_NAME ||
    params.sales_page_signup_framing_v5_experiment_name === SALES_SIGNUP_V5_EXPERIMENT_NAME ||
    Boolean(
      params[`$feature/${SALES_SIGNUP_V5_FEATURE_FLAG}`] ||
      params[`$feature_flag_${SALES_SIGNUP_V5_FEATURE_FLAG}`] ||
      params.sales_page_signup_framing_v5_variant ||
      params.sales_page_signup_framing_v5_route_variant,
    );
  const featureFlag = isV5 ? SALES_SIGNUP_V5_FEATURE_FLAG : SALES_SIGNUP_V4_FEATURE_FLAG;
  const experimentName = isV5 ? SALES_SIGNUP_V5_EXPERIMENT_NAME : SALES_SIGNUP_V4_EXPERIMENT_NAME;
  const experimentNameKey = `${experimentName}_experiment_name`;
  const variantKey = `${experimentName}_variant`;
  const routeVariantKey = `${experimentName}_route_variant`;
  const normalizeRouteVariant = isV5
    ? normalizeSalesSignupV5RouteVariant
    : normalizeSalesSignupV4RouteVariant;
  const normalizePostHogVariant = isV5
    ? normalizeSalesSignupV5PostHogVariant
    : normalizeSalesSignupV4PostHogVariant;
  const rawPostHogVariant =
    params[`$feature/${featureFlag}`] ||
    params[`$feature_flag_${featureFlag}`] ||
    params[variantKey] ||
    params.sales_page_signup_framing_variant;
  const rawRouteVariant =
    params[routeVariantKey] || params.sales_page_signup_framing_variant || rawPostHogVariant;
  const routeVariant = normalizeRouteVariant(rawRouteVariant);
  const posthogVariant =
    normalizePostHogVariant(rawPostHogVariant) || normalizePostHogVariant(routeVariant);

  if (!posthogVariant && !routeVariant) {
    return {};
  }

  const resolvedRouteVariant =
    routeVariant ||
    (posthogVariant === 'control' ? (isV5 ? 'email_only' : 'full_form') : posthogVariant);

  return {
    feature_flag: featureFlag,
    experiment_name: experimentName,
    experiment_variant: resolvedRouteVariant,
    [experimentNameKey]: experimentName,
    sales_page_signup_framing_variant: resolvedRouteVariant,
    [variantKey]: posthogVariant || resolvedRouteVariant,
    [routeVariantKey]: resolvedRouteVariant,
    sales_page_form_variant:
      params.sales_page_form_variant || getSalesSignupV4FormVariant(resolvedRouteVariant),
    sales_page_framing_variant:
      params.sales_page_framing_variant || getSalesSignupV4FramingVariant(resolvedRouteVariant),
    sales_page_design_variant:
      params.sales_page_design_variant || getSalesSignupV4DesignVariant(resolvedRouteVariant),
    [`$feature_flag_${featureFlag}`]: posthogVariant || resolvedRouteVariant,
    [`$feature/${featureFlag}`]: posthogVariant || resolvedRouteVariant,
  };
};

// Current Starter and Light offers. Keep the adopted Starter attribution key stable.
const VARIANT_CONFIG = {
  'discounttest-2': {
    // No trial for discount variant
    trialDays: 0,
    // Monthly pricing with 50% discount
    monthlyPrice: 49, // After coupon
    monthlyPriceText: '$49/month',
    monthlyOriginalPrice: '$99', // Show strikethrough

    monthlyPlanKey: 'no_trial',
    // Annual pricing with 50% discount (coupon applies to annual too)
    // Original annual: $990, with 50% off: $495
    annualPrice: 495,

    annualPriceText: '$495/year',
    annualOriginalPrice: '$990',
    annualMonthlyEquivalent: '$41.25/month',
    annualSavingsText: '50% off + 2 months free',
    annualPlanKey: 'annual', // Annual never has trial, use same plan for all variants
    annualTrialDays: 0,
    // Display text
    priceText: 'only $49/month',
    originalPrice: '$99',
    ctaText: 'Claim 50% Off Now',
    annualCtaText: 'Claim 50% Off - $495/year',
    headerText: 'Test Your Strategy Before Risking Real Capital',
    subheaderText: '50% off for a limited time',
    termsText: 'subscription',
    annualTermsText: 'subscription',
    planKey: 'no_trial', // Legacy field
    promoCode: 'STARTER50OFF', // Auto-apply coupon - works for both monthly and annual
    showUrgencyTimer: true,
    // Note: urgency banner adds "Was/Now" prefixes, so priceText should include "only"
  },
  // Light Plan variant - Pure price test vs discounttest-2 Starter.
  // Identical UX to discounttest-2 (discount framing, no trial).
  // Only the underlying product + price differ: Light $49 → $24 vs Starter $99 → $49.

  // Light includes one deployment, but its AI usage remains materially lower than Starter.
  light: {
    trialDays: 0,
    // Monthly pricing with 50% discount
    monthlyPrice: 24,
    monthlyPriceText: '$24/month',
    monthlyOriginalPrice: '$49',

    monthlyPlanKey: 'default',
    // Annual base price is ten regular months ($490), then LIGHT50OFF applies
    // the same 50% promotion for an exact $245 annual checkout amount.
    annualPrice: 245,

    annualPriceText: '$245/year',
    annualOriginalPrice: '$490',
    annualMonthlyEquivalent: '$20.42/month',
    annualSavingsText: '50% off + 2 months free',
    annualPlanKey: 'annual',
    annualTrialDays: 0,
    // Display text
    priceText: 'only $24/month',
    originalPrice: '$49',
    ctaText: 'Claim 50% Off Now',
    annualCtaText: 'Claim 50% Off - $245/year',
    headerText: 'Build Your Trading Bot With AI',
    subheaderText: '50% off for a limited time',
    termsText: 'subscription',
    planKey: 'default',
    planDisplayName: 'BotSpot Light',
    promoCode: 'LIGHT50OFF',
    showUrgencyTimer: true,
  },
};

// Default to control if variant not found
const getVariantConfig = (variant) => VARIANT_CONFIG[variant] || VARIANT_CONFIG['discounttest-2'];

const planHasDiscount = (plan) => plan?.discountedAmount != null || Boolean(plan?.appliedPromotion);

const findDiscountedPlanForCheckout = (catalog, productSlug, planKey) => {
  const product = catalog?.products?.find((p) => p.slug === productSlug);
  if (!product) return null;

  return (
    product.plans?.find((plan) => plan.planKey === planKey && planHasDiscount(plan)) ||
    product.plans?.find(planHasDiscount) ||
    null
  );
};

// The catalog prices a dead code with the plan's automatic promotion (for
// example LIGHT50OFF). A requested code only counts when the catalog applied
// that exact code, so a dead code is never shown or sent as if it worked.
const findPlanDiscountedByCode = (catalog, productSlug, planKey, code) => {
  const plan = findDiscountedPlanForCheckout(catalog, productSlug, planKey);
  const appliedCode = plan?.appliedPromotion?.code;
  if (appliedCode && appliedCode.toUpperCase() !== String(code).trim().toUpperCase()) {
    return null;
  }
  return plan;
};

// Plain words for a link code that did not apply. The server message can carry
// a raw timestamp, so only its status is used here.
const LINK_CODE_REASONS = {
  expired: 'has expired',
  not_started: 'is not active yet',
  not_applicable: 'does not apply to this plan',
};
const linkCodeReason = (status) => LINK_CODE_REASONS[status] || 'is not a valid code';

const couponStateFromPlan = (code, plan) => ({
  code: code.toUpperCase(),
  description: buildCouponDescription(plan?.appliedPromotion),
  discountedAmount: plan?.discountedAmount,
  originalAmount: plan?.amount,
  promotion: plan?.appliedPromotion || null,
});

const getVariantAmountCents = (variantConfig, billingPeriod) => {
  const isAnnual = billingPeriod === 'annual';
  const explicitAmountCents = isAnnual
    ? variantConfig.annualAmountCents
    : variantConfig.monthlyAmountCents;
  if (Number.isFinite(Number(explicitAmountCents))) {
    return Math.max(0, Math.round(Number(explicitAmountCents)));
  }

  const amountDollars = isAnnual ? variantConfig.annualPrice : variantConfig.monthlyPrice;
  return dollarsToCents(amountDollars, isAnnual ? 99000 : 9900);
};

const getVariantOriginalAmountCents = (variantConfig, billingPeriod, fallbackAmountCents) => {
  const explicitAmountCents =
    billingPeriod === 'annual'
      ? variantConfig.annualOriginalAmountCents
      : variantConfig.monthlyOriginalAmountCents;
  if (Number.isFinite(Number(explicitAmountCents))) {
    return Math.max(0, Math.round(Number(explicitAmountCents)));
  }
  return fallbackAmountCents;
};

const getCheckoutMonetaryEventProps = ({
  variantConfig,
  billingPeriod,
  stripeMode,
  appliedCoupon,
}) => {
  const planAmountCents = getVariantAmountCents(variantConfig, billingPeriod);
  const configuredOriginalAmountCents = getVariantOriginalAmountCents(
    variantConfig,
    billingPeriod,
    planAmountCents,
  );
  const originalAmountCents = appliedCoupon?.originalAmount ?? configuredOriginalAmountCents;
  const chargeAmountCents = Math.max(0, appliedCoupon?.discountedAmount ?? planAmountCents);
  const amountDueTodayCents = Math.max(0, stripeMode === 'setup' ? 0 : chargeAmountCents);
  const discountAmountCents = Math.max(0, originalAmountCents - chargeAmountCents);

  const potentialRevenueCents = originalAmountCents;

  return {
    currency: 'USD',
    checkout_currency: 'USD',
    checkout_plan_amount_cents: planAmountCents,
    checkout_original_amount_cents: originalAmountCents,
    checkout_amount_due_today_cents: amountDueTodayCents,
    checkout_amount_due_today_usd: centsToUsd(amountDueTodayCents),
    checkout_discount_amount_cents: discountAmountCents,
    checkout_discount_amount_usd: centsToUsd(discountAmountCents),
    checkout_potential_revenue_cents: potentialRevenueCents,
    checkout_potential_revenue_usd: centsToUsd(potentialRevenueCents),
    checkout_initial_charge_cents: amountDueTodayCents,
    checkout_future_recurring_amount_cents: originalAmountCents,
    checkout_has_initial_charge: amountDueTodayCents > 0,
    checkout_is_setup_mode: stripeMode === 'setup',
    checkout_revenue_tracking_basis:
      stripeMode === 'setup' ? 'payment_method_setup_then_future_subscription' : 'initial_payment',
  };
};

// Collapsible promo code input (mirrors CouponCodeSection from CheckoutPage)
function CouponCodeInput({
  onApply,
  onClearValidationError,
  isValidating,
  validationError,
  disabled,
}) {
  const [inputValue, setInputValue] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef(null);
  const errorId = 'checkout-promo-error';
  const couponInputHints = {
    autoComplete: 'nope',
    autoCorrect: 'off',
    autoCapitalize: 'characters',
    spellCheck: false,
    inputMode: 'text',
    name: 'checkout-discount',
    'data-1p-ignore': 'true',
    'data-lpignore': 'true',
    'data-form-type': 'other',
  };

  const handleApplyClick = () => {
    if (inputValue.trim() && !isValidating && !disabled) {
      onApply(inputValue.trim().toUpperCase());
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    if (validationError) {
      onClearValidationError?.();
    }
  };

  const handleCancel = () => {
    setIsExpanded(false);
    setInputValue('');
    if (validationError) {
      onClearValidationError?.();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApplyClick();
    }
  };

  useEffect(() => {
    if (!isExpanded) return undefined;
    const focusId = window.requestAnimationFrame(() => {
      try {
        inputRef.current?.focus({ preventScroll: true });
      } catch {
        inputRef.current?.focus();
      }
    });
    return () => window.cancelAnimationFrame(focusId);
  }, [isExpanded]);

  if (!isExpanded) {
    return (
      <button
        type="button"
        className={`coupon-code-toggle${''}`}
        onClick={() => setIsExpanded(true)}
        disabled={disabled}
        aria-label={undefined}
      >
        {<FaTag aria-hidden="true" />}
        {'Have a promo code?'}
      </button>
    );
  }

  return (
    <div className="coupon-code-form">
      <div className="coupon-code-form__input-wrapper">
        <input
          type="text"
          className={`coupon-code-form__input${validationError ? ' coupon-code-form__input--error' : ''}`}
          placeholder="Enter promo code"
          aria-label="Promo code"
          aria-invalid={Boolean(validationError)}
          aria-describedby={validationError ? errorId : undefined}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={disabled || isValidating}
          {...couponInputHints}
        />
        <button
          type="button"
          className="coupon-code-form__submit"
          onClick={handleApplyClick}
          disabled={!inputValue.trim() || isValidating || disabled}
          aria-label={isValidating ? 'Applying promo code' : 'Apply promo code'}
          aria-busy={isValidating}
        >
          {isValidating ? <Spinner animation="border" size="sm" /> : <FaCheck aria-hidden="true" />}
        </button>
      </div>
      {validationError && (
        <div id={errorId} className="coupon-code-form__error" role="alert">
          {validationError}
        </div>
      )}
      <button
        type="button"
        className="coupon-code-form__cancel"
        onClick={handleCancel}
        disabled={isValidating}
      >
        Cancel
      </button>
    </div>
  );
}

const buildTermsPayload = () => {
  const timestamp = new Date().toISOString();
  return {
    docType: TERMS_DOC_TYPE,
    version: timestamp.split('T')[0],
    acceptedAt: timestamp,
    hash: null,
  };
};

/**
 * StarterCheckoutForm - Inner form component rendered within Stripe Elements.
 *
 * IMPORTANT: This component receives billingPeriod and onBillingPeriodChange as props
 * because the billing period affects which Stripe mode (setup vs payment) is used.
 * The mode must be known BEFORE Elements initialization, so state is lifted to parent.
 *
 * @param {string} billingPeriod - 'monthly' or 'annual' (controlled by parent)
 * @param {function} onBillingPeriodChange - Callback to change billing period
 * @param {string} stripeMode - 'setup' or 'payment' (determined by parent based on trial days)
 */
function isTransientCheckoutBlock(reason) {
  return (
    reason === 'stripe_not_ready' ||
    reason === 'payment_element_incomplete' ||
    reason === 'coupon_validating' ||
    reason === 'payment_total_updating'
  );
}

function StarterCheckoutForm({
  productSlug,
  initialUserInfo,
  variantConfig,
  experimentVariant,
  checkoutTheme = 'dark',
  billingPeriod,
  onBillingPeriodChange,
  stripeMode,
  appliedCoupon,
  onAppliedCouponChange,
  savedMethods,
  checkoutUser,
  checkoutExperienceExperiment = null,
  linkCouponNotice = null,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshPaymentStatus } = usePaymentStatus();
  const { isAuthenticated, user, getApiToken, handleCustomJwtAuth } = checkoutUser;
  const authenticatedAccountEmail = String(checkoutUser?.userProfile?.email || user?.email || '')
    .trim()
    .toLowerCase();

  const [contactData, setContactData] = useState(() => ({
    name: initialUserInfo?.name || '',
    email: initialUserInfo?.email || '',
    phone: initialUserInfo?.phone || '',
    howFoundUs: initialUserInfo?.howFoundUs || '',
    howFoundUsOtherText: initialUserInfo?.howFoundUsOtherText || '',
  }));

  useEffect(() => {
    setContactData((prev) => ({
      name: prev.name || initialUserInfo?.name || '',
      email: prev.email || initialUserInfo?.email || '',
      phone: prev.phone || initialUserInfo?.phone || '',
      howFoundUs: prev.howFoundUs || initialUserInfo?.howFoundUs || '',
      howFoundUsOtherText: prev.howFoundUsOtherText || initialUserInfo?.howFoundUsOtherText || '',
    }));
  }, [
    initialUserInfo?.name,
    initialUserInfo?.email,
    initialUserInfo?.phone,
    initialUserInfo?.howFoundUs,
    initialUserInfo?.howFoundUsOtherText,
  ]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [smsConsentAccepted, setSmsConsentAccepted] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const lastValidationErrorRef = useRef('');
  // A pay click that lands while Stripe is still settling is remembered and
  // sent when the form becomes ready. The 2026-09-21 hosted gate left the
  // button focused on a finished form because that click was discarded and
  // the error was cleared.
  const pendingBuyerSubmitRef = useRef(false);
  const [paymentElementReady, setPaymentElementReady] = useState(false);

  const walletPaymentRequest = null;
  const [availableWalletMethods, setAvailableWalletMethods] = useState([]);

  const [paymentMethodAvailability, setPaymentMethodAvailability] = useState(null);
  const [paymentMethodAvailabilityChecked, setPaymentMethodAvailabilityChecked] = useState(false);
  const [paymentMethodAvailabilityError, setPaymentMethodAvailabilityError] = useState('');
  const [, setGooglePayButtonReady] = useState(false);
  const [, setGooglePayButtonFailed] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('wallet');
  const [selectedSavedPaymentMethodId, setSelectedSavedPaymentMethodId] = useState(null);
  const bankVerificationMethod = BANK_VERIFICATION_METHOD;

  const [showTooltip, setShowTooltip] = useState(false);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [couponValidationError, setCouponValidationError] = useState('');
  const buttonRef = useRef(null);

  const checkoutPageLoadedTrackedRef = useRef(false);
  const checkoutReadyTrackedRef = useRef(false);
  const paymentElementReadyTrackedRef = useRef(false);
  const paymentElementCompleteTrackedRef = useRef(false);

  const handleSubmitRef = useRef(null);
  const googlePayWalletClickRef = useRef(null);
  const trackCheckoutDiagnosticRef = useRef(null);

  const checkoutExperimentName = ACTIVE_EXPERIMENTS.LIGHT_VS_STARTER_CHECKOUT.name;
  const checkoutExperienceEventProps = useMemo(() => {
    const properties = {};
    if (checkoutExperienceExperiment?.enrolled)
      Object.assign(properties, {
        checkout_experience_experiment_name: ACTIVE_EXPERIMENTS.STRIPE_HOSTED_CHECKOUT_2026_08.name,
        checkout_experience_feature_flag:
          ACTIVE_EXPERIMENTS.STRIPE_HOSTED_CHECKOUT_2026_08.featureFlag,
        checkout_experience_variant: checkoutExperienceExperiment.variant,
        checkout_experience_subject_id: checkoutExperienceExperiment.subjectId,
        checkout_experience_assignment_id: checkoutExperienceExperiment.assignmentId,
        checkout_experience_surface: 'plan_checkout',
        checkout_experience_exposed_at: checkoutExperienceExperiment.exposedAt,
        [`$feature/${ACTIVE_EXPERIMENTS.STRIPE_HOSTED_CHECKOUT_2026_08.featureFlag}`]:
          checkoutExperienceExperiment.variant,
      });
    return properties;
  }, [checkoutExperienceExperiment]);

  // Use variant config values - extract both monthly and annual pricing
  const {
    // Trial (only applies to monthly)
    trialDays: monthlyTrialDays,
    annualTrialDays = 0,
    // Monthly pricing
    monthlyPriceText,
    monthlyOriginalPrice,
    monthlyPlanKey,
    // Annual pricing
    annualPriceText,
    annualOriginalPrice,
    annualMonthlyEquivalent,
    annualSavingsText,
    annualPlanKey,
    // Display text
    priceText: defaultPriceText,
    originalPrice: defaultOriginalPrice,
    ctaText: monthlyCtaText,
    annualCtaText,
    termsText: monthlyTermsText,
    annualTermsText,
    // Legacy planKey for backwards compatibility
    planKey: legacyPlanKey,
    promoCode,
    promotionDuration,
    showUrgencyTimer,
  } = variantConfig;

  // Compute values based on selected billing period
  const isAnnual = billingPeriod === 'annual';
  const trialDays = isAnnual ? annualTrialDays : monthlyTrialDays;
  const priceText = isAnnual ? annualPriceText : monthlyPriceText || defaultPriceText;
  const originalPrice = isAnnual
    ? annualOriginalPrice
    : monthlyOriginalPrice || defaultOriginalPrice;
  const ctaText = isAnnual ? annualCtaText || monthlyCtaText : monthlyCtaText;
  const termsText = isAnnual ? annualTermsText || monthlyTermsText : monthlyTermsText;
  const planKey = isAnnual ? annualPlanKey : monthlyPlanKey || legacyPlanKey;
  const planDisplayName =
    variantConfig.planDisplayName ||
    (experimentVariant === 'light' || productSlug === 'light-plan'
      ? 'BotSpot Light'
      : 'Starter Plan');

  const configuredPlanAmountCents = useMemo(
    () => getVariantAmountCents(variantConfig, billingPeriod),
    [billingPeriod, variantConfig],
  );
  const elementsAmountCents =
    stripeMode === 'payment'
      ? (appliedCoupon?.discountedAmount ?? configuredPlanAmountCents)
      : null;
  const elementsContextKey = `${stripeMode}:${elementsAmountCents ?? 'setup'}`;
  const confirmedElementsContextKeyRef = useRef(elementsContextKey);
  const pendingElementsContextKeyRef = useRef(elementsContextKey);

  pendingElementsContextKeyRef.current = elementsContextKey;
  if (!elements) {
    confirmedElementsContextKeyRef.current = elementsContextKey;
  }
  const elementsUpdating = Boolean(false);

  const configuredOriginalAmountCents = useMemo(
    () => getVariantOriginalAmountCents(variantConfig, billingPeriod, configuredPlanAmountCents),
    [billingPeriod, configuredPlanAmountCents, variantConfig],
  );
  const checkoutOriginalAmountCents =
    appliedCoupon?.originalAmount ?? configuredOriginalAmountCents;
  const checkoutChargeAmountCents = Math.max(
    0,
    appliedCoupon?.discountedAmount ?? configuredPlanAmountCents,
  );
  const checkoutAmountDueTodayCents = Math.max(
    0,
    appliedCoupon?.discountedAmount ?? (stripeMode === 'setup' ? 0 : configuredPlanAmountCents),
  );
  const checkoutDiscountAmountCents = Math.max(
    0,
    checkoutOriginalAmountCents - checkoutChargeAmountCents,
  );
  const isRecurringPlan = variantConfig.setupFutureUsage !== false && termsText !== 'purchase';

  // What the ad platforms are told this cart is worth. Monthly plans count the
  // assumed months of tenure (Rob's 2.5), annual plans count the year they
  // already pay for. See utils/checkoutAnalyticsValue.ts.
  const checkoutAnalyticsValueUsd = checkoutAnalyticsValue([
    {
      plan: {
        amount: checkoutChargeAmountCents,
        interval: isRecurringPlan ? (isAnnual ? 'year' : 'month') : null,
        metadata: {},
      },
      raw: { quantity: 1 },
      product: { metadata: { isRecurring: isRecurringPlan } },
    },
  ]);
  const effectivePromotionDuration =
    appliedCoupon?.promotion?.duration || promotionDuration || null;

  const checkoutFutureRecurringAmountCents = !isRecurringPlan
    ? 0
    : effectivePromotionDuration === 'forever'
      ? checkoutChargeAmountCents
      : appliedCoupon || effectivePromotionDuration
        ? checkoutOriginalAmountCents
        : checkoutChargeAmountCents;
  const checkoutMonetaryEventProps = useMemo(
    () => ({
      currency: 'USD',
      checkout_currency: 'USD',
      checkout_plan_amount_cents: configuredPlanAmountCents,
      checkout_original_amount_cents: checkoutOriginalAmountCents,
      checkout_amount_due_today_cents: checkoutAmountDueTodayCents,
      checkout_amount_due_today_usd: centsToUsd(checkoutAmountDueTodayCents),
      checkout_discount_amount_cents: checkoutDiscountAmountCents,
      checkout_discount_amount_usd: centsToUsd(checkoutDiscountAmountCents),
      checkout_potential_revenue_cents: checkoutOriginalAmountCents,
      checkout_potential_revenue_usd: centsToUsd(checkoutOriginalAmountCents),
      checkout_initial_charge_cents: checkoutAmountDueTodayCents,
      checkout_future_recurring_amount_cents: checkoutOriginalAmountCents,
      checkout_has_initial_charge: checkoutAmountDueTodayCents > 0,
      checkout_is_setup_mode: stripeMode === 'setup',
      checkout_revenue_tracking_basis:
        stripeMode === 'setup'
          ? 'payment_method_setup_then_future_subscription'
          : 'initial_payment',
    }),
    [
      checkoutAmountDueTodayCents,
      checkoutChargeAmountCents,
      checkoutDiscountAmountCents,
      checkoutFutureRecurringAmountCents,
      checkoutOriginalAmountCents,
      configuredPlanAmountCents,
      stripeMode,
    ],
  );

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const redirectParam = queryParams.get('redirect');
  const postSignupDestinationParam = queryParams.get('postSignupDestination');
  const leadTransactionId = queryParams.get('leadId') || null;
  const promptPrefillParam = queryParams.get('promptPrefill') || '';
  const variantParam = queryParams.get('variant') || experimentVariant;
  const salesPageExperimentParams = useMemo(
    () =>
      SALES_PAGE_EXPERIMENT_PARAM_KEYS.reduce((props, key) => {
        const value = queryParams.get(key);
        if (value) {
          props[key] = value;
        }
        return props;
      }, {}),
    [queryParams],
  );
  const salesSignupV4Attribution = useMemo(
    () => buildSalesSignupAttribution(salesPageExperimentParams),
    [salesPageExperimentParams],
  );
  const couponParam = queryParams.get('coupon') || promoCode; // Use variant promoCode if no URL param
  // Cross-funnel handoff: the marketplace CTA guard passes successUrl when a user
  // clicks "Start My Strategy" unpaid, pointing back to the marketplace detail
  // flow. Without this, every completed checkout lands on /ai-agent regardless
  // of where the user started, stranding marketplace-origin buyers on the wrong
  // page. When source=marketplace, we prefer this URL over the /ai-agent default.
  const successUrlParam = queryParams.get('successUrl');
  const checkoutSourceParam = queryParams.get('source');

  const promptFromSession = useMemo(() => loadPromptFromSession(), []);
  const activePrompt = promptFromSession || promptPrefillParam;
  const checkoutNextStep = useMemo(() => {
    if (checkoutSourceParam === 'marketplace') {
      return {
        title: 'Return to the strategy you picked',
        primary: 'After payment, BotSpot sends you back to the marketplace strategy you chose.',
        secondary:
          'Start in paper trading first, review the bot settings, then decide if you want to connect a broker.',
      };
    }

    if (activePrompt) {
      return {
        title: 'Continue from your AI prompt',
        primary:
          'After payment, BotSpot opens the AI Agent with the trading idea you already submitted.',
        secondary:
          'You can review the generated logic, run tests, and start with paper trading before using real money.',
      };
    }

    if (redirectParam === 'starter_plan') {
      return {
        title: 'Go straight to the AI Agent',
        primary:
          'After payment, BotSpot opens the AI Agent so you can build and test your trading workflow.',
        secondary: 'Start with paper trading, watch the logs, and stop any bot at any time.',
      };
    }

    return {
      title: 'Your BotSpot account opens instantly',
      primary: 'After payment, you go to the AI Agent to build and test your trading workflow.',
      secondary:
        'Start with paper trading, connect a broker when ready, and stop any bot at any time.',
    };
  }, [activePrompt, checkoutSourceParam, redirectParam]);

  const utmData = useMemo(() => getUtmParamsForApi(location.search), [location.search]);
  const attributionParams = useMemo(() => getAttributionParams(utmData), [utmData]);

  // Coupon code handlers
  const handleApplyCoupon = useCallback(
    async (code) => {
      if (!code || isValidatingCoupon) return;
      setIsValidatingCoupon(true);
      setCouponValidationError('');
      try {
        const token = isAuthenticated ? await getApiToken() : null;
        const catalog = await validatePromoCode(code, token);
        const discountedPlan = findPlanDiscountedByCode(catalog, productSlug, planKey, code);
        if (discountedPlan) {
          onAppliedCouponChange(couponStateFromPlan(code, discountedPlan));
        } else {
          setCouponValidationError(
            catalog?.promotionCodeValidation?.message ||
              'This promo code is invalid or has expired.',
          );
        }
      } catch (err) {
        setCouponValidationError('This promo code is invalid or has expired.');
      } finally {
        setIsValidatingCoupon(false);
      }
    },
    [isAuthenticated, getApiToken, isValidatingCoupon, onAppliedCouponChange, planKey, productSlug],
  );

  const handleRemoveCoupon = useCallback(() => {
    onAppliedCouponChange(null);
    setCouponValidationError('');
  }, [onAppliedCouponChange]);

  // Effective coupon: manually entered takes priority over auto-apply variant coupon
  const effectiveCoupon = appliedCoupon?.code || couponParam;

  const checkoutAnalyticsBase = useMemo(
    () => ({
      source: location.pathname,
      page_path: location.pathname,
      checkout_type: 'landing_checkout_page',
      flow_context: 'sales_ai_landing_checkout',
      plan_type: productSlug || 'starter-plan',
      product_slug: productSlug || 'starter-plan',
      plan_key: planKey,
      billing_period: billingPeriod,
      stripe_mode: stripeMode,
      experiment_variant: experimentVariant,
      experiment_name: checkoutExperimentName,
      ...checkoutExperienceEventProps,
      lead_transaction_id: leadTransactionId,
      checkout_source: checkoutSourceParam || null,
      redirect_param: redirectParam || null,
      success_url_present: Boolean(successUrlParam),
      prompt_provided: Boolean(activePrompt),
      has_coupon: Boolean(effectiveCoupon),
      coupon_code: effectiveCoupon || null,
      how_found_us: contactData.howFoundUs || null,
      how_found_us_details: contactData.howFoundUsOtherText?.trim() || null,
      ...checkoutMonetaryEventProps,
      ...salesPageExperimentParams,
      ...salesSignupV4Attribution,
      ...utmData,
    }),
    [
      activePrompt,
      billingPeriod,
      checkoutExperienceEventProps,
      checkoutExperimentName,
      checkoutMonetaryEventProps,
      checkoutSourceParam,
      contactData.howFoundUs,
      contactData.howFoundUsOtherText,
      effectiveCoupon,
      experimentVariant,
      leadTransactionId,
      location.pathname,
      planKey,
      productSlug,
      redirectParam,
      salesPageExperimentParams,
      salesSignupV4Attribution,
      stripeMode,
      successUrlParam,
      utmData,
    ],
  );

  const trackCheckoutDiagnostic = useCallback(
    (event, properties = {}) => {
      pushEvent({
        ...checkoutAnalyticsBase,
        event,
        ...{},
        ...properties,
      });
    },
    [checkoutAnalyticsBase],
  );

  useEffect(() => {
    trackCheckoutDiagnosticRef.current = trackCheckoutDiagnostic;
  }, [trackCheckoutDiagnostic]);

  useEffect(() => {
    setPaymentMethodAvailability(null);
    setPaymentMethodAvailabilityChecked(true);
    setPaymentMethodAvailabilityError('');
    return undefined;
  }, []);

  const savedPaymentMethods = useMemo(
    () => savedMethods?.paymentMethods || [],
    [savedMethods?.paymentMethods],
  );
  const hasSavedPaymentMethods = Boolean(
    savedMethods?.hasMethods && savedPaymentMethods.length > 0,
  );
  const selectedSavedPaymentMethod = useMemo(
    () => savedPaymentMethods.find((method) => method.id === selectedSavedPaymentMethodId) || null,
    [savedPaymentMethods, selectedSavedPaymentMethodId],
  );
  const preferredSavedPaymentMethod = useMemo(
    () => savedPaymentMethods.find((method) => method.isDefault) || savedPaymentMethods[0] || null,
    [savedPaymentMethods],
  );

  useEffect(() => {
    if (!hasSavedPaymentMethods) {
      if (selectedSavedPaymentMethodId) {
        setSelectedSavedPaymentMethodId(null);
      }
      if (selectedPaymentMethod === 'saved') {
        setSelectedPaymentMethod('card');
      }
      return;
    }

    if (
      !selectedSavedPaymentMethod ||
      !savedPaymentMethods.some((method) => method.id === selectedSavedPaymentMethodId)
    ) {
      setSelectedSavedPaymentMethodId(preferredSavedPaymentMethod?.id || null);
    }
  }, [
    hasSavedPaymentMethods,
    preferredSavedPaymentMethod?.id,
    savedPaymentMethods,
    selectedPaymentMethod,
    selectedSavedPaymentMethod,
    selectedSavedPaymentMethodId,
  ]);

  const howFoundUsComplete =
    contactData.howFoundUs &&
    (contactData.howFoundUs !== 'other' || contactData.howFoundUsOtherText?.trim());
  const hasPrefilledHowFoundUs =
    initialUserInfo?.howFoundUs &&
    (initialUserInfo.howFoundUs !== 'other' || initialUserInfo.howFoundUsOtherText?.trim());

  const contactComplete =
    contactData.name.trim() &&
    contactData.email.trim() &&
    contactData.phone.trim() &&
    howFoundUsComplete;
  const contactWasPrefilledOnLoad = Boolean(
    initialUserInfo?.name && initialUserInfo?.email && initialUserInfo?.phone,
  );

  useEffect(() => {
    if (checkoutPageLoadedTrackedRef.current) return;
    checkoutPageLoadedTrackedRef.current = true;
    trackCheckoutDiagnostic('checkout_page_loaded', {
      user_type: isAuthenticated ? 'registered' : 'guest',
      has_prefilled_contact: Boolean(initialUserInfo?.email || initialUserInfo?.phone),
      has_prefilled_how_found_us: Boolean(hasPrefilledHowFoundUs),
    });
  }, [
    hasPrefilledHowFoundUs,
    initialUserInfo?.email,
    initialUserInfo?.phone,
    isAuthenticated,
    trackCheckoutDiagnostic,
  ]);

  /**
   * Begin checkout: the buyer reached this checkout surface. Fired on arrival,
   * not on the marketing-page button click, so it means "reached checkout".
   * `trackBeginCheckout` keys the guard on surface, product, plan and
   * experiment arm, so re-render, tab refocus and the checkout overlay opening
   * and closing all report exactly one conversion per checkout session.
   */
  useEffect(() => {
    trackBeginCheckout({
      dedupeKey: [
        'plan_checkout',
        productSlug,
        planKey || 'default',
        billingPeriod,
        checkoutExperienceEventProps.checkout_experience_variant || 'control',
      ].join(':'),
      source: location.pathname,
      checkout_surface: 'plan_checkout',
      user_type: isAuthenticated ? 'registered' : 'guest',
      plan_type: productSlug,
      plan_key: planKey || null,
      billing_period: billingPeriod,
      flow_context: 'sales_ai_landing_checkout',
      experiment_name: checkoutExperimentName,
      experiment_variant: experimentVariant,
      value: checkoutAnalyticsValueUsd,
      ...checkoutMonetaryEventProps,
      ...checkoutExperienceEventProps,
      ...utmData,
    });
  }, [
    billingPeriod,
    checkoutAmountDueTodayCents,
    checkoutExperienceEventProps,
    checkoutExperimentName,
    checkoutMonetaryEventProps,
    experimentVariant,
    isAuthenticated,
    location.pathname,
    planKey,
    productSlug,
    utmData,
  ]);

  useEffect(() => {
    if (checkoutReadyTrackedRef.current) return;
    const resolvedPaymentMethod =
      selectedPaymentMethod === 'saved'
        ? 'saved_card'
        : selectedPaymentMethod === 'bank'
          ? 'bank'
          : selectedPaymentMethod;
    const selectedPaymentReady =
      (resolvedPaymentMethod === 'wallet' && false) ||
      (resolvedPaymentMethod === 'saved_card' && Boolean(selectedSavedPaymentMethodId)) ||
      (resolvedPaymentMethod === 'card' && paymentElementReady) ||
      (resolvedPaymentMethod === 'bank' && Boolean(stripe)) ||
      false;
    if (
      !resolvedPaymentMethod ||
      !contactComplete ||
      !termsAccepted ||
      !selectedPaymentReady ||
      !stripe ||
      !elements
    )
      return;
    checkoutReadyTrackedRef.current = true;
    trackCheckoutDiagnostic('checkout_form_ready', {
      user_type: isAuthenticated ? 'registered' : 'guest',
      payment_source: resolvedPaymentMethod,
    });
  }, [
    contactComplete,
    elements,
    false,
    false,
    false,
    isAuthenticated,
    paymentElementReady,
    selectedPaymentMethod,
    selectedSavedPaymentMethodId,
    stripe,
    termsAccepted,
    trackCheckoutDiagnostic,
  ]);

  // Keep checkout validation observable without changing which fields are required.
  const getValidationState = useCallback(() => {
    const hasName = Boolean(contactData.name.trim());
    const hasEmail = Boolean(contactData.email.trim());
    const hasPhone = Boolean(contactData.phone.trim());
    const hasHowFoundUs = Boolean(contactData.howFoundUs);
    const hasHowFoundUsDetails = Boolean(contactData.howFoundUsOtherText?.trim());

    const usesBankPayment = selectedPaymentMethod === 'bank';
    const usesSavedPaymentMethod = selectedPaymentMethod === 'saved';

    const currentPaymentElementReady = usesBankPayment
      ? Boolean(stripe)
      : usesSavedPaymentMethod
        ? Boolean(selectedSavedPaymentMethodId)
        : paymentElementReady;

    const buildState = (validationReason, validationMessage) => ({
      validation_reason: validationReason,
      validation_message: validationMessage,
      has_name: hasName,
      has_email: hasEmail,
      has_phone: hasPhone,
      sms_consent_accepted: Boolean(smsConsentAccepted),
      has_how_found_us: hasHowFoundUs,
      how_found_us_value: contactData.howFoundUs || null,
      has_how_found_us_details: hasHowFoundUsDetails,
      terms_accepted: Boolean(termsAccepted),
      stripe_ready: Boolean(stripe),
      elements_ready: Boolean(elements),
      payment_element_ready: Boolean(currentPaymentElementReady),
      dynamic_payment_element_ready: Boolean(false),
      saved_payment_method_selected: Boolean(selectedSavedPaymentMethodId),
      selected_payment_method: selectedPaymentMethod,
      processing: Boolean(processing),
      coupon_validating: Boolean(isValidatingCoupon),
      checkout_recovery_required: Boolean(false),
      contact_complete: Boolean(contactComplete),
      user_type: isAuthenticated ? 'registered' : 'guest',
    });

    if (!hasName) return buildState('missing_name', 'Please enter your full name');
    if (!hasEmail) return buildState('missing_email', 'Please enter your email address');
    if (!hasPhone) return buildState('missing_phone', 'Please enter your phone number');
    if (!hasHowFoundUs) {
      return buildState('missing_how_found_us', 'Please tell us how you found BotSpot');
    }
    if (contactData.howFoundUs === 'other' && !hasHowFoundUsDetails) {
      return buildState('missing_how_found_us_details', 'Please describe how you found BotSpot');
    }
    if (!termsAccepted) return buildState('terms_not_accepted', TERMS_ACCEPTANCE_SHORT_MESSAGE);
    if (isValidatingCoupon)
      return buildState('coupon_validating', 'Please wait for your promo code to finish applying.');
    if (elementsUpdating)
      return buildState('payment_total_updating', 'Please wait while the payment total updates.');
    if (!stripe) return buildState('stripe_not_ready', 'Payment system is still loading...');
    if (paymentMethodAvailabilityError) {
      return buildState('payment_method_availability_failed', paymentMethodAvailabilityError);
    }

    if (!currentPaymentElementReady)
      return buildState('payment_element_incomplete', 'Please complete your payment information');
    if (processing) return buildState('already_processing', 'Processing your payment...');
    return buildState(null, '');
  }, [
    false,
    contactComplete,
    contactData.name,
    contactData.email,
    contactData.phone,
    contactData.howFoundUs,
    contactData.howFoundUsOtherText,
    false,
    false,
    elements,
    elementsUpdating,
    isAuthenticated,
    isValidatingCoupon,
    paymentMethodAvailabilityError,
    paymentElementReady,
    processing,
    true,
    selectedPaymentMethod,
    selectedSavedPaymentMethodId,
    smsConsentAccepted,
    stripe,
    termsAccepted,
  ]);

  // Get validation message for tooltip based on current form state
  const getValidationMessage = useCallback(
    () => getValidationState().validation_message,
    [getValidationState],
  );

  // Handle button click when form is incomplete - show validation feedback
  const handleButtonClick = useCallback(
    (e) => {
      const validationState = getValidationState();
      trackCheckoutDiagnostic('checkout_cta_clicked', validationState);

      const validationMsg = validationState.validation_message;
      if (validationMsg) {
        if (isTransientCheckoutBlock(validationState.validation_reason)) {
          pendingBuyerSubmitRef.current = true;
        }
        trackCheckoutDiagnostic('checkout_validation_click_blocked', validationState);
        e.preventDefault();
        setValidationAttempted(true);
        lastValidationErrorRef.current = validationMsg;
        setError(validationMsg);
        setShowTooltip(true);

        if (validationState.validation_reason?.startsWith('missing_')) {
          const contactSection = document.querySelector('.checkout-contact-fields');
          if (contactSection) {
            const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
            contactSection.scrollIntoView({
              behavior: reduceMotion ? 'auto' : 'smooth',
              block: 'center',
            });
          }

          const invalidControlSelectors = {
            missing_name: 'input[name="name"]',
            missing_email: 'input[name="email"]',
            missing_phone: 'input[name="phone"]',
            missing_how_found_us: '#how-found-us-select',
            missing_how_found_us_details: '.how-found-us-other-input',
          };
          const invalidControl = document.querySelector(
            invalidControlSelectors[validationState.validation_reason],
          );
          if (invalidControl instanceof HTMLElement) {
            window.requestAnimationFrame(() => invalidControl.focus({ preventScroll: true }));
          }
        }

        if (validationState.validation_reason === 'terms_not_accepted') {
          setTermsError(true);
          const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
          const termsGroup = document.querySelector('.terms-checkbox-group');
          if (termsGroup) {
            termsGroup.scrollIntoView({
              behavior: reduceMotion ? 'auto' : 'smooth',
              block: 'center',
            });
            termsGroup.classList.add('shake');
            setTimeout(() => termsGroup.classList.remove('shake'), 500);
          }
          const termsCheckbox = document.getElementById('landing-checkout-terms');
          if (termsCheckbox instanceof HTMLElement) {
            window.requestAnimationFrame(() => termsCheckbox.focus({ preventScroll: true }));
          }
        }

        // Hide tooltip after 3 seconds
        setTimeout(() => setShowTooltip(false), 3000);
      }
    },
    [getValidationState, trackCheckoutDiagnostic],
  );

  useEffect(() => {
    if (!validationAttempted || processing || error !== lastValidationErrorRef.current) {
      return;
    }

    const nextValidationState = getValidationState();
    const nextValidationMessage = nextValidationState.validation_message;
    lastValidationErrorRef.current = nextValidationMessage;
    setError(nextValidationMessage);
    setTermsError(nextValidationState.validation_reason === 'terms_not_accepted');
  }, [error, getValidationState, processing, validationAttempted]);

  const maybeConfirmInvoicePayment = useCallback(
    async (conversionResult) => {
      if (!conversionResult?.requiresAction || !conversionResult?.invoiceClientSecret) {
        return;
      }
      if (!stripe) {
        throw new Error('Payment processor is not ready to finalize your payment.');
      }

      const { error: invoiceError } = await confirmPaymentWithClient(stripe, null, {
        clientSecret: conversionResult.invoiceClientSecret,
        redirect: 'if_required',
        confirmParams: {
          return_url: window.location.href,
        },
      });

      if (invoiceError) {
        throw new Error(
          invoiceError.message || 'Additional payment confirmation failed. Please try again.',
        );
      }
      refreshPaymentStatus({ forceRefresh: true });
    },
    [refreshPaymentStatus, stripe],
  );

  const handleContactChange = useCallback((field, value) => {
    setContactData((prev) => ({ ...prev, [field]: value }));
    if (field === 'phone') {
      // A decision is tied to the exact number shown beside the disclosure.
      setSmsConsentAccepted(false);
    }
  }, []);

  const handleSmsConsentChange = useCallback((accepted) => {
    setSmsConsentAccepted(Boolean(accepted));
  }, []);

  // Handle billing period toggle with analytics tracking
  // Uses prop callback since state is lifted to parent for Stripe mode calculation
  const handleBillingPeriodChange = useCallback(
    (newPeriod) => {
      if (newPeriod === billingPeriod) return; // No change

      const nextStripeMode =
        newPeriod === 'annual'
          ? variantConfig.annualTrialDays > 0 || appliedCoupon?.discountedAmount === 0
            ? 'setup'
            : 'payment'
          : variantConfig.trialDays > 0 || appliedCoupon?.discountedAmount === 0
            ? 'setup'
            : 'payment';
      const nextTermsText =
        newPeriod === 'annual' ? annualTermsText || monthlyTermsText : monthlyTermsText;
      const nextMonetaryEventProps = getCheckoutMonetaryEventProps({
        variantConfig,
        billingPeriod: newPeriod,
        stripeMode: nextStripeMode,
        // The parent clears plan-specific coupon cents when billing changes.
        appliedCoupon: null,
        promotionDuration,
        termsText: nextTermsText,
      });

      // Track billing period change for conversion attribution
      pushEvent({
        event: 'billing_period_changed',
        billing_period: newPeriod,
        previous_period: billingPeriod,
        plan_type: productSlug || 'starter-plan',
        experiment_variant: experimentVariant,
        ...checkoutExperienceEventProps,
        ...nextMonetaryEventProps,
        page: 'landing_checkout',
        // Track if this changes the Stripe mode (trial, $0 promo setup, or immediate payment)
        stripe_mode_change: stripeMode !== nextStripeMode,
      });

      // Same-mode amount changes update Elements in place. Mode changes remount it.
      onBillingPeriodChange(newPeriod);
    },
    [
      appliedCoupon,
      annualTermsText,
      billingPeriod,
      checkoutExperienceEventProps,
      experimentVariant,
      monthlyTermsText,
      onBillingPeriodChange,
      productSlug,
      promotionDuration,
      stripeMode,
      variantConfig,
    ],
  );

  const buildAiBuilderRedirect = useCallback(() => {
    if (redirectParam !== 'starter_plan') {
      return null;
    }

    // Redirect to AI Agent instead of deprecated AI Bot Builder
    // The handoff is already set via setAgentHandoff() and will be picked up by AI Agent
    const params = new URLSearchParams();
    if (leadTransactionId) {
      params.set('leadId', leadTransactionId);
    }
    if (variantParam) {
      params.set('variant', variantParam);
    }

    const queryString = params.toString();
    return `/ai-agent${queryString ? `?${queryString}` : ''}`;
  }, [leadTransactionId, redirectParam, variantParam]);

  const attemptAutoLoginFlow = useCallback(
    async (conversion, stripeProof = {}) => {
      try {
        const autoLoginBody = {};
        if (conversion.subscriptionId) autoLoginBody.subscription_id = conversion.subscriptionId;
        if (contactData.email) autoLoginBody.email = contactData.email;
        if (conversion.customerId) autoLoginBody.customer_id = conversion.customerId;
        const paymentIntentId = stripeProof.paymentIntentId || conversion.paymentIntentId;
        const paymentIntentClientSecret =
          stripeProof.paymentIntentClientSecret || conversion.paymentIntentClientSecret;
        const setupIntentId = stripeProof.setupIntentId || conversion.setupIntentId;
        const setupIntentClientSecret =
          stripeProof.setupIntentClientSecret || conversion.setupIntentClientSecret;
        if (paymentIntentId) autoLoginBody.payment_intent_id = paymentIntentId;
        if (paymentIntentClientSecret)
          autoLoginBody.payment_intent_client_secret = paymentIntentClientSecret;
        if (setupIntentId) autoLoginBody.setup_intent_id = setupIntentId;
        if (setupIntentClientSecret)
          autoLoginBody.setup_intent_client_secret = setupIntentClientSecret;

        // Check for handoff with conversation context (saved when user clicked upgrade from AI Agent)
        // This allows us to return the user to their specific conversation after checkout
        const pendingHandoff = getAgentHandoff();
        const handoffConversationId = pendingHandoff?.conversationId;

        // Build the FINAL destination URL (where user should end up after any upsells).
        //
        // Priority order:
        //   1. AI Agent conversation handoff (user was mid-conversation when they hit a paywall)
        //   2. Marketplace-origin successUrl (user clicked "Start My Strategy" on /marketplace/strategy/:id)
        //   3. AI Agent fallback (default lead-form funnel)
        let finalDestination;
        if (postSignupDestinationParam) {
          finalDestination = sanitizeInternalDestination(postSignupDestinationParam);
        } else if (handoffConversationId) {
          // Return to the specific conversation the user was on
          const params = new URLSearchParams();
          if (leadTransactionId) params.set('leadId', leadTransactionId);
          if (variantParam) params.set('variant', variantParam);
          const queryString = params.toString();
          finalDestination = `/ai-agent/${handoffConversationId}${queryString ? `?${queryString}` : ''}`;
          if (SHOULD_LOG_CHECKOUT_DEBUG) {
            console.log(
              '[StarterCheckoutPage] Final destination conversation:',
              handoffConversationId,
            );
          }
        } else if (checkoutSourceParam === 'marketplace' && successUrlParam) {
          // Marketplace funnel: user came from clicking a strategy CTA unpaid.
          // Send them back to the marketplace detail flow (via the successUrl the
          // guard built), which will show a resumeAction toast prompting them to
          // click Start My Strategy again — now as a paying user, so the deploy
          // flow in StrategyDetailsPage.handleDeploy proceeds to /deploy.
          //
          // We keep same-origin validation by parsing the successUrl and only
          // using the pathname+search (strips any attacker-controlled origin).
          try {
            const parsed = new URL(successUrlParam, window.location.origin);
            if (parsed.origin === window.location.origin) {
              finalDestination = `${parsed.pathname}${parsed.search}`;
              if (SHOULD_LOG_CHECKOUT_DEBUG) {
                console.log(
                  '[StarterCheckoutPage] Final destination marketplace:',
                  finalDestination,
                );
              }
            } else {
              console.warn(
                '[StarterCheckoutPage] Cross-origin successUrl rejected:',
                parsed.origin,
              );
              finalDestination = '/marketplace';
            }
          } catch (urlErr) {
            console.warn(
              '[StarterCheckoutPage] Invalid successUrl, falling back:',
              urlErr?.message,
            );
            finalDestination = '/marketplace';
          }
        } else {
          // Fallback: check for starter_plan redirect param or default to /ai-agent
          const builderRedirectTarget = buildAiBuilderRedirect();
          const defaultRedirectParams = new URLSearchParams();
          if (leadTransactionId) defaultRedirectParams.set('leadId', leadTransactionId);
          if (variantParam) defaultRedirectParams.set('variant', variantParam);
          const defaultRedirectQuery = defaultRedirectParams.toString();
          const defaultRedirectTarget = `/ai-agent${defaultRedirectQuery ? `?${defaultRedirectQuery}` : ''}`;
          finalDestination = builderRedirectTarget || defaultRedirectTarget;
        }

        const redirectTarget = buildPostPurchaseChallengePath(
          finalDestination,
          'starter_light_checkout_post_purchase',
        );
        if (SHOULD_LOG_CHECKOUT_DEBUG) {
          console.log(
            '[StarterCheckoutPage] Redirecting to Challenge, final destination:',
            finalDestination,
          );
        }

        const normalizedCheckoutEmail = String(contactData.email || '')
          .trim()
          .toLowerCase();
        const checkoutMatchesAuthenticatedAccount = Boolean(
          isAuthenticated &&
          authenticatedAccountEmail &&
          normalizedCheckoutEmail === authenticatedAccountEmail,
        );

        if (checkoutMatchesAuthenticatedAccount) {
          navigate(redirectTarget, { replace: true });
          return;
        }

        autoLoginBody.redirectTo = redirectTarget;

        // NOTE: Do NOT clear handoff here - the AI Agent page will clear it after consuming the prompt
        // Previously clearing here caused the prompt to be lost because the user goes through:
        // checkout → magic link → bootcamp upsell → AI Agent, and by the time they reach AI Agent
        // the handoff was already cleared
        // See: AiAgent.js line ~384 where clearAgentHandoff() is called after processing

        const autoLoginResponse = await fetchWithTimeout(
          `${API_URL}/stripe/autoLoginAfterPurchase`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(autoLoginBody),
          },
          AUTO_LOGIN_FETCH_TIMEOUT_MS,
        );

        if (!autoLoginResponse.ok) {
          throw new Error(`Auto-login request failed: ${autoLoginResponse.status}`);
        }

        const autoLoginData = await autoLoginResponse.json();

        if (
          await completeStripeAutoLogin(autoLoginData, {
            handleCustomJwtAuth,
            fallbackUrl: redirectTarget,
            navigate,
          })
        ) {
          return;
        }

        // Fallback navigation if no magic link returned
        navigate(redirectTarget, { replace: true });
      } catch (autoLoginError) {
        console.error('[StarterCheckoutPage] Auto-login error:', autoLoginError);
        // On error, still redirect to AI Agent page (use handoff if available)
        const pendingHandoff = getAgentHandoff();
        const handoffConversationId = pendingHandoff?.conversationId;
        let fallbackRedirect;
        if (handoffConversationId) {
          fallbackRedirect = `/ai-agent/${handoffConversationId}`;
        } else {
          const fallbackParams = new URLSearchParams();
          if (leadTransactionId) fallbackParams.set('leadId', leadTransactionId);
          if (variantParam) fallbackParams.set('variant', variantParam);
          const fallbackQuery = fallbackParams.toString();
          fallbackRedirect = `/ai-agent${fallbackQuery ? `?${fallbackQuery}` : ''}`;
        }
        navigate(
          buildPostPurchaseChallengePath(
            postSignupDestinationParam
              ? sanitizeInternalDestination(postSignupDestinationParam)
              : buildAiBuilderRedirect() || fallbackRedirect,
            'starter_light_checkout_auto_login_recovery',
          ),
          { replace: true },
        );
      }
    },
    [
      buildAiBuilderRedirect,
      postSignupDestinationParam,
      authenticatedAccountEmail,
      checkoutSourceParam,
      contactData.email,
      contactData.name,
      contactData.phone,
      handleCustomJwtAuth,
      isAuthenticated,
      leadTransactionId,
      navigate,
      planKey,
      productSlug,
      successUrlParam,
      variantParam,
    ],
  );

  const handleSubmit = useCallback(
    async (eventOrOptions) => {
      const isDomSubmit = Boolean(eventOrOptions?.preventDefault);
      if (isDomSubmit) {
        eventOrOptions.preventDefault();
      }

      const submitOptions = isDomSubmit ? {} : eventOrOptions || {};
      const paymentSource =
        submitOptions.paymentSource ||
        (selectedPaymentMethod === 'saved'
          ? 'saved_card'
          : selectedPaymentMethod === 'bank'
            ? 'bank'
            : 'card');
      const isWalletPayment = paymentSource === 'wallet';
      const isBankPayment = paymentSource === 'bank';
      const isSavedPayment = paymentSource === 'saved_card';

      const intentPaymentType = isBankPayment ? 'us_bank_account' : 'card';
      const useSubscriptionPaymentIntentMode = stripeMode === 'payment' && !isBankPayment;
      const walletEvent = submitOptions.walletEvent || null;
      const walletPaymentMethodId = submitOptions.walletPaymentMethodId || null;
      const walletType = submitOptions.walletType || walletEvent?.expressPaymentType || null;

      const isElementsWalletPayment = isWalletPayment && !walletPaymentMethodId;
      const selectedPaymentReady = !paymentSource
        ? false
        : isBankPayment
          ? Boolean(stripe)
          : isSavedPayment
            ? Boolean(selectedSavedPaymentMethodId)
            : paymentElementReady;
      let walletCompletionReported = false;
      const completeWalletPayment = (status) => {
        if (walletCompletionReported || typeof submitOptions.walletComplete !== 'function') return;
        walletCompletionReported = true;
        submitOptions.walletComplete(status);
      };
      const failWalletPayment = (message, reason = 'fail') => {
        completeWalletPayment('fail');
        if (typeof walletEvent?.paymentFailed === 'function') {
          walletEvent.paymentFailed({ reason, message });
          trackCheckoutDiagnostic('wallet_confirm_failed', {
            wallet_type: walletType,
            failure_reason: reason,
            has_message: Boolean(message),
          });
        }
      };

      if (processing) {
        trackCheckoutDiagnostic('checkout_submit_ignored', {
          blocked_reason: 'already_processing',
          payment_source: paymentSource,
          wallet_type: walletType,
        });
        failWalletPayment('Payment is already processing.');
        return;
      }

      setError('');
      trackCheckoutDiagnostic('checkout_submit_clicked', {
        user_type: isAuthenticated ? 'registered' : 'guest',
        contact_complete: Boolean(contactComplete),
        terms_accepted: Boolean(termsAccepted),
        payment_element_ready: Boolean(
          isWalletPayment ? walletPaymentMethodId || elements : selectedPaymentReady,
        ),
        saved_payment_method_selected: Boolean(selectedSavedPaymentMethodId),
        coupon_validating: Boolean(isValidatingCoupon),
        payment_source: paymentSource,
        wallet_type: walletType,
      });

      if (isValidatingCoupon) {
        pendingBuyerSubmitRef.current = true;
        trackCheckoutDiagnostic('checkout_submit_blocked', {
          blocked_reason: 'coupon_validating',
          payment_source: paymentSource,
          wallet_type: walletType,
        });
        setError('Please wait for your promo code to finish applying.');
        failWalletPayment('Promo code is still applying.');
        return;
      }

      if (!contactComplete) {
        setValidationAttempted(true);
        trackCheckoutDiagnostic('checkout_submit_blocked', {
          blocked_reason: 'contact_incomplete',
          has_name: Boolean(contactData.name.trim()),
          has_email: Boolean(contactData.email.trim()),
          has_phone: Boolean(contactData.phone.trim()),
          has_how_found_us: Boolean(howFoundUsComplete),
          payment_source: paymentSource,
          wallet_type: walletType,
        });
        const contactMessage = howFoundUsComplete
          ? 'Please fill in your name, email, and phone number to continue.'
          : 'Please tell us how you found BotSpot before completing your purchase.';
        setError(contactMessage);
        failWalletPayment(contactMessage);
        return;
      }

      if (!termsAccepted) {
        setValidationAttempted(true);
        setTermsError(true);
        trackCheckoutDiagnostic('checkout_submit_blocked', {
          blocked_reason: 'terms_not_accepted',
          payment_source: paymentSource,
          wallet_type: walletType,
        });
        setError(TERMS_ACCEPTANCE_ERROR_MESSAGE);
        failWalletPayment(TERMS_ACCEPTANCE_ERROR_MESSAGE);
        return;
      }

      if (!paymentSource) {
        const paymentMessage = paymentMethodAvailabilityError || 'Please choose a payment method.';
        setValidationAttempted(true);
        trackCheckoutDiagnostic('checkout_submit_blocked', {
          blocked_reason: paymentMethodAvailabilityError
            ? 'payment_method_availability_failed'
            : 'payment_method_missing',
          payment_source: null,
          wallet_type: walletType,
        });
        setError(paymentMessage);
        failWalletPayment(paymentMessage);
        return;
      }

      if (
        !stripe ||
        (isElementsWalletPayment && !elements) ||
        (isSavedPayment && !selectedPaymentReady) ||
        (!isWalletPayment &&
          !isBankPayment &&
          !isSavedPayment &&
          !false &&
          (!elements || !selectedPaymentReady)) ||
        (isBankPayment && !false && !selectedPaymentReady)
      ) {
        pendingBuyerSubmitRef.current = true;
        setValidationAttempted(true);
        trackCheckoutDiagnostic('checkout_submit_blocked', {
          blocked_reason: 'stripe_not_ready',
          stripe_ready: Boolean(stripe),
          elements_ready: Boolean(elements),
          payment_element_ready: Boolean(
            isWalletPayment ? walletPaymentMethodId || elements : selectedPaymentReady,
          ),
          saved_payment_method_selected: Boolean(selectedSavedPaymentMethodId),
          payment_source: paymentSource,
          wallet_type: walletType,
        });
        setError('Payment form is not ready yet. Please wait a moment and try again.');
        failWalletPayment('Payment form is not ready yet.');
        return;
      }

      setProcessing(true);

      try {
        if (isWalletPayment) {
          trackCheckoutDiagnostic('wallet_confirm_started', {
            payment_source: paymentSource,
            wallet_type: walletType,
          });
        }

        if (isElementsWalletPayment || false) {
          const { error: submitError } = await elements.submit();
          if (submitError) {
            trackCheckoutDiagnostic('stripe_elements_submit_failed', {
              stripe_error_type: submitError.type || null,
              payment_source: paymentSource,
              wallet_type: walletType,
            });
            const submitMessage =
              submitError.message || 'Failed to submit Apple Pay or Google Pay details.';
            setError(submitMessage);
            failWalletPayment(submitMessage, 'invalid_payment_data');
            setProcessing(false);
            return;
          }
        }

        identifyUserFromLeadCapture(
          contactData.email,
          {
            name: contactData.name,
            phone: contactData.phone,
            lead_type: 'sales_ai_checkout',
            form_step: 'payment_submission',
            checkout_type: 'landing_checkout_page',
          },
          'landing_checkout_page',
        );

        const userId = isAuthenticated ? user.sub : `temp_${Date.now()}`;
        const token = isAuthenticated ? await getApiToken() : null;
        const checkoutTransactionId = generateCheckoutTransactionId(
          null,
          null,
          `landing_checkout_${Date.now()}`,
        );
        const checkoutAttemptId = createCheckoutAttemptId('starter_checkout');

        // Same canonical event, same guard: a buyer who reached checkout and then
        // submitted payment is one begin_checkout, not two.
        trackBeginCheckout({
          dedupeKey: [
            'plan_checkout',
            productSlug,
            planKey || 'default',
            billingPeriod,
            checkoutExperienceEventProps.checkout_experience_variant || 'control',
          ].join(':'),
          transactionId: checkoutTransactionId,
          source: location.pathname,
          userId,
          user_type: isAuthenticated ? 'registered' : 'guest',
          plan_type: productSlug || 'starter-plan',
          flow_context: 'sales_ai_landing_checkout',
          contact_email: contactData.email,
          contact_name: contactData.name,
          contact_phone: contactData.phone,
          lead_transaction_id: leadTransactionId,
          experiment_variant: experimentVariant,
          experiment_name: checkoutExperimentName,
          ...checkoutExperienceEventProps,
          ...checkoutMonetaryEventProps,
          ...salesPageExperimentParams,
          ...salesSignupV4Attribution,
          // Annual Plan Toggle - track billing period selection
          billing_period: billingPeriod,
          plan_key: planKey,
          payment_source: paymentSource,
          wallet_type: walletType,
          ...utmData,
        });

        const termsPayload = buildTermsPayload();
        const affiliateCode = getAffiliateCodeForApi();
        const affiliateFields = affiliateCode
          ? { affiliateCode, affiliate_code: affiliateCode, aff: affiliateCode }
          : {};
        const smsConsentPayload = buildSmsConsentPayload({
          source: 'landing_checkout_page',
          phoneNumber: contactData.phone,
          accepted: smsConsentAccepted,
        });

        if (isSavedPayment) {
          if (!selectedSavedPaymentMethodId) {
            setError('Please choose a saved payment method or select another payment option.');
            setProcessing(false);
            return;
          }

          const conversion = await convertLeadToTrial(
            {
              email: contactData.email,
              customerName: contactData.name,
              phoneNumber: contactData.phone,
              paymentMethodId: selectedSavedPaymentMethodId,
              checkoutAttemptId,
              terms: termsPayload,
              ...(smsConsentPayload ? { smsConsent: smsConsentPayload } : {}),
              productSlugs: [productSlug || 'starter-plan'],
              ...salesSignupV4Attribution,
              flowContext: 'sales_ai_landing_checkout',
              cartMetadata: {
                productSlug: productSlug || 'starter-plan',
                experiment_variant: experimentVariant,
                ...checkoutExperienceEventProps,
                ...checkoutMonetaryEventProps,
                ...salesPageExperimentParams,
                ...salesSignupV4Attribution,
                billing_period: billingPeriod,
                stripe_mode: stripeMode,
                payment_source: paymentSource,
                saved_payment_method_id: selectedSavedPaymentMethodId,
              },
              howFoundUs: contactData.howFoundUs || null,
              howFoundUsOtherText: contactData.howFoundUsOtherText?.trim() || null,
              items: [
                {
                  productSlug: productSlug || 'starter-plan',
                  planKey,
                  quantity: 1,
                },
              ],
              ...(effectiveCoupon ? { promoCode: effectiveCoupon } : {}),
            },
            { apiUrl: API_URL, authToken: token },
          );

          await maybeConfirmInvoicePayment(conversion);
          pushEvent({
            event: ANALYTICS_EVENTS.PAYMENT_SUCCESS,
            transaction_id: conversion.subscriptionId || selectedSavedPaymentMethodId,
            value: checkoutAnalyticsValueUsd,
            currency: 'USD',
            plan_type: productSlug || 'starter-plan',
            source: location.pathname,
            userId,
            user_type: isAuthenticated ? 'registered' : 'guest',
            customerId: conversion.customerId,
            paymentMethodId: selectedSavedPaymentMethodId,
            lead_transaction_id: leadTransactionId,
            experiment_variant: experimentVariant,
            experiment_name: checkoutExperimentName,
            ...checkoutExperienceEventProps,
            ...checkoutMonetaryEventProps,
            checkout_payment_result: 'saved_payment_method_completed',
            payment_source: paymentSource,
            how_found_us: contactData.howFoundUs || null,
            how_found_us_details: contactData.howFoundUsOtherText?.trim() || null,
            ...salesPageExperimentParams,
            ...salesSignupV4Attribution,
            ...utmData,
          });

          try {
            await refreshPaymentStatus({ forceRefresh: true });
          } catch (refreshError) {
            console.warn('[StarterCheckoutPage] Failed to refresh payment status:', refreshError);
          }

          await attemptAutoLoginFlow(conversion, {
            paymentMethodId: selectedSavedPaymentMethodId,
          });
          return;
        }

        // Create SetupIntent or PaymentIntent based on stripe mode
        // - 'setup' mode (trial): Backend should return SetupIntent (seti_...) for future charge
        // - 'payment' mode (no trial): Backend should return PaymentIntent (pi_...) for immediate charge
        if (SHOULD_LOG_CHECKOUT_DEBUG) {
          console.log(
            `[StarterCheckoutPage] Submitting with stripeMode=${stripeMode}, subscriptionPaymentIntentMode=${useSubscriptionPaymentIntentMode}`,
          );
          console.info('[StarterCheckoutPage] Affiliate payload handoff', {
            affiliateCode: affiliateCode || null,
            hasAffiliateFields: Boolean(affiliateCode),
            email: contactData.email,
            productSlug: productSlug || 'starter-plan',
            planKey,
          });
        }

        trackCheckoutDiagnostic('checkout_payment_intent_request_started', {
          transaction_id: checkoutTransactionId,
          userId,
          user_type: isAuthenticated ? 'registered' : 'guest',
        });

        let setupResponse;
        try {
          setupResponse = await fetchWithTimeout(
            `${API_URL}/stripe/create-payment-intent`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Request-Id': checkoutAttemptId,
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({
                productSlugs: [productSlug || 'starter-plan'],
                ...getWebsiteThemeAttribution(),
                ...affiliateFields,
                email: contactData.email,
                customerName: contactData.name,
                phoneNumber: contactData.phone,
                paymentType: intentPaymentType,
                ...(isBankPayment ? { bankVerificationMethod } : {}),
                terms: termsPayload,
                ...(smsConsentPayload ? { smsConsent: smsConsentPayload } : {}),
                checkoutAttemptId,
                leadTransactionId,
                flowContext: 'sales_ai_landing_checkout',
                ...salesSignupV4Attribution,
                cartMetadata: {
                  productSlug: productSlug || 'starter-plan',
                  promptProvided: Boolean(activePrompt),
                  variant: variantParam || experimentVariant || undefined,
                  source: 'sales_ai_prompt_landing_checkout',
                  experiment_variant: experimentVariant,
                  ...checkoutExperienceEventProps,
                  ...checkoutMonetaryEventProps,
                  ...salesPageExperimentParams,
                  ...salesSignupV4Attribution,
                  billing_period: billingPeriod, // Track monthly vs annual selection
                  stripe_mode: stripeMode, // Track which Stripe mode frontend expects
                  payment_source: paymentSource,
                  wallet_type: walletType,
                  bank_verification_method: isBankPayment ? bankVerificationMethod : undefined,
                },
                howFoundUs: contactData.howFoundUs || null,
                howFoundUsOtherText: contactData.howFoundUsOtherText?.trim() || null,
                ...affiliateFields,
                // Pass planKey to select the correct ProductPlan based on billing period and variant
                items: [
                  {
                    productSlug: productSlug || 'starter-plan',
                    planKey: planKey, // Computed from billingPeriod: 'default'/'annual' for control, 'no_trial'/'annual_no_trial' for others
                    quantity: 1,
                  },
                ],
                // Promo code support for ABC pricing test (discount variant)
                ...(effectiveCoupon ? { promoCode: effectiveCoupon } : {}),
                forceSubscriptionCreation: true,
                // CRITICAL: subscriptionPaymentIntentMode tells backend which intent type to return
                // - true: Return PaymentIntent for immediate charge (payment mode, no trial)
                // - false: Return SetupIntent for future charge (setup mode, with trial)
                // Bank checkout uses SetupIntent first so Stripe Financial Connections can open
                // before the slower subscription/invoice creation path.
                subscriptionPaymentIntentMode: useSubscriptionPaymentIntentMode,
                ...(utmData?.utm_source ? { utmSource: utmData.utm_source } : {}),
                ...(utmData?.utm_medium ? { utmMedium: utmData.utm_medium } : {}),
                ...(utmData?.utm_campaign ? { utmCampaign: utmData.utm_campaign } : {}),
                ...(utmData?.utm_term ? { utmTerm: utmData.utm_term } : {}),
                ...(utmData?.utm_content ? { utmContent: utmData.utm_content } : {}),
                ...(utmData?.referrer ? { referrer: utmData.referrer } : {}),
                landingPage:
                  utmData?.landing_page ||
                  sessionStorage.getItem('landingPage') ||
                  document.referrer,
                pagePath: window.location.pathname,
                pageTitle: document.title,
                ...attributionParams,
              }),
            },
            PAYMENT_INTENT_FETCH_TIMEOUT_MS,
          );
        } catch (requestError) {
          throw requestError;
        }

        let setupData = null;
        try {
          setupData = await setupResponse.json();
        } catch (parseError) {
          throw parseError;
        }
        const checkoutResponse = classifyCheckoutResponse(setupData);
        if (setupResponse.ok && checkoutResponse.kind === 'completed_subscription') {
          trackCheckoutDiagnostic('credit_covered_checkout_completed', {
            transaction_id: checkoutTransactionId,
            subscription_id: checkoutResponse.subscriptionId,
            subscription_status: checkoutResponse.status,
            invoice_amount_due_cents: Number(setupData?.invoiceAmountDue || 0),
            invoice_total_cents: Number(setupData?.invoiceTotal || 0),
            plan_type: productSlug || 'starter-plan',
            payment_source: paymentSource,
            wallet_type: walletType,
          });
          try {
            await refreshPaymentStatus({ forceRefresh: true });
          } catch (refreshError) {
            console.warn('[StarterCheckoutPage] Failed to refresh payment status:', refreshError);
          }

          await attemptAutoLoginFlow(setupData);
          return;
        }
        if (!setupResponse.ok || !setupData?.clientSecret) {
          trackCheckoutDiagnostic('checkout_payment_intent_request_failed', {
            transaction_id: checkoutTransactionId,
            status_code: setupResponse.status,
            has_message: Boolean(setupData?.message),
            ...{},
            payment_source: paymentSource,
            wallet_type: walletType,
          });

          const setupMessage =
            setupData?.message || 'Failed to initialize payment. Please try again.';
          setError(setupMessage);
          failWalletPayment(setupMessage);
          setProcessing(false);
          return;
        }

        // Determine which confirmation flow to use based on stripeMode
        // - 'payment' mode: Use confirmPayment() for PaymentIntent (pi_...)
        // - 'setup' mode: Use confirmSetup() for SetupIntent (seti_...)
        //
        // VALIDATION: Verify backend returned the expected intent type
        const isPaymentIntent = setupData.clientSecret?.startsWith('pi_');
        const isSetupIntent = setupData.clientSecret?.startsWith('seti_');

        trackCheckoutDiagnostic('checkout_payment_intent_created', {
          transaction_id: checkoutTransactionId,
          ...{},
          returned_intent_type: isPaymentIntent
            ? 'payment_intent'
            : isSetupIntent
              ? 'setup_intent'
              : 'unknown',
          expected_intent_type: useSubscriptionPaymentIntentMode
            ? 'payment_intent'
            : 'setup_intent',
          subscription_id_present: Boolean(setupData.subscriptionId),
          customer_id_present: Boolean(setupData.customerId),
          payment_source: paymentSource,
          wallet_type: walletType,
        });

        if (SHOULD_LOG_CHECKOUT_DEBUG) {
          console.log(
            `[StarterCheckoutPage] Backend returned: ${isPaymentIntent ? 'PaymentIntent' : isSetupIntent ? 'SetupIntent' : 'unknown'}, expected: ${useSubscriptionPaymentIntentMode ? 'PaymentIntent' : 'SetupIntent'}`,
          );
        }

        const intentTypeMismatch =
          (useSubscriptionPaymentIntentMode && !isPaymentIntent) ||
          (!useSubscriptionPaymentIntentMode && !isSetupIntent);

        if (intentTypeMismatch) {
          console.warn(
            `[StarterCheckoutPage] ⚠️ Stripe mode mismatch! Frontend mode: ${stripeMode}, Backend returned: ${isPaymentIntent ? 'pi_' : isSetupIntent ? 'seti_' : 'unknown'}`,
          );
        }

        // Use the frontend-determined mode (stripeMode) since Elements was initialized with it
        // The backend MUST return matching intent type or Stripe will throw an error
        const usingPaymentIntent = useSubscriptionPaymentIntentMode;

        const bankBillingDetails = isBankPayment
          ? {
              name: contactData.name,
              email: contactData.email,
              phone: contactData.phone,
            }
          : null;
        const bankCollectionOptions = bankBillingDetails
          ? {
              clientSecret: setupData.clientSecret,
              params: {
                payment_method_type: 'us_bank_account',
                payment_method_data: {
                  billing_details: bankBillingDetails,
                },
              },
              expand: ['payment_method'],
            }
          : null;
        const walletBillingDetails = isWalletPayment
          ? {
              name:
                submitOptions.walletBillingDetails?.name ||
                walletEvent?.billingDetails?.name ||
                contactData.name,
              email:
                submitOptions.walletBillingDetails?.email ||
                walletEvent?.billingDetails?.email ||
                contactData.email,
              phone:
                submitOptions.walletBillingDetails?.phone ||
                walletEvent?.billingDetails?.phone ||
                contactData.phone,
            }
          : null;
        const walletConfirmParams = isWalletPayment
          ? {
              return_url: `${window.location.origin}/set-password?email=${encodeURIComponent(
                walletBillingDetails.email || contactData.email,
              )}&name=${encodeURIComponent(walletBillingDetails.name || contactData.name)}&redirectTo=onboarding&source_flow=landing_checkout_wallet_redirect`,
              ...(walletPaymentMethodId ? { payment_method: walletPaymentMethodId } : {}),
            }
          : null;

        if (usingPaymentIntent) {
          if (!isWalletPayment && !isBankPayment) {
            const { error: submitError } = await elements.submit();
            if (submitError) {
              trackCheckoutDiagnostic('stripe_elements_submit_failed', {
                transaction_id: checkoutTransactionId,
                stripe_error_type: submitError.type || null,
                payment_source: paymentSource,
                wallet_type: walletType,
              });
              setError(submitError.message || 'Failed to submit payment details.');
              setProcessing(false);
              return;
            }
          }

          trackCheckoutDiagnostic('stripe_confirm_started', {
            transaction_id: checkoutTransactionId,
            intent_type: 'payment_intent',
            payment_source: paymentSource,
            wallet_type: walletType,
          });

          let stripeError = null;
          let paymentIntent = null;
          if (isBankPayment && !false) {
            if (
              typeof stripe.collectBankAccountForPayment !== 'function' ||
              typeof stripe.confirmUsBankAccountPayment !== 'function'
            ) {
              trackCheckoutDiagnostic('stripe_confirm_failed', {
                transaction_id: checkoutTransactionId,
                intent_type: 'payment_intent',
                stripe_error_type: 'bank_api_unavailable',
                payment_source: paymentSource,
                wallet_type: walletType,
              });
              const bankUnavailableMessage =
                'Bank payment is not available in this browser. Please use card instead.';
              setError(bankUnavailableMessage);
              setProcessing(false);
              return;
            }

            trackCheckoutDiagnostic('stripe_bank_account_collection_started', {
              transaction_id: checkoutTransactionId,
              intent_type: 'payment_intent',
              payment_source: paymentSource,
            });

            const { error: bankCollectionError, paymentIntent: collectedPaymentIntent } =
              await stripe.collectBankAccountForPayment(bankCollectionOptions);

            if (bankCollectionError) {
              trackCheckoutDiagnostic('stripe_confirm_failed', {
                transaction_id: checkoutTransactionId,
                intent_type: 'payment_intent',
                stripe_error_type: bankCollectionError.type || 'bank_collection_failed',
                payment_source: paymentSource,
                wallet_type: walletType,
              });
              setError(
                bankCollectionError.message ||
                  'Bank connection failed. Please try again or use card.',
              );
              setProcessing(false);
              return;
            }

            if (
              !collectedPaymentIntent ||
              collectedPaymentIntent.status === 'requires_payment_method'
            ) {
              trackCheckoutDiagnostic('checkout_submit_blocked', {
                transaction_id: checkoutTransactionId,
                blocked_reason: 'bank_connection_cancelled',
                payment_source: paymentSource,
              });
              setError('Bank connection was cancelled. Choose bank again or use card.');
              setProcessing(false);
              return;
            }

            if (collectedPaymentIntent.status === 'requires_confirmation') {
              const bankConfirmResult = await stripe.confirmUsBankAccountPayment(
                setupData.clientSecret,
              );
              stripeError = bankConfirmResult.error || null;
              paymentIntent = bankConfirmResult.paymentIntent || null;
            } else {
              paymentIntent = collectedPaymentIntent;
            }
          } else {
            const confirmResult = await confirmPaymentWithClient(
              stripe,
              isElementsWalletPayment ? elements : isWalletPayment ? null : elements,
              {
                clientSecret: setupData.clientSecret,
                redirect: 'if_required',
                ...(isWalletPayment
                  ? {
                      confirmParams: walletConfirmParams,
                    }
                  : {}),
                ...{},
                ...{},
              },
            );
            stripeError = confirmResult.error || null;
            paymentIntent = confirmResult.paymentIntent || null;
          }

          if (stripeError) {
            trackCheckoutDiagnostic('stripe_confirm_failed', {
              transaction_id: checkoutTransactionId,
              intent_type: 'payment_intent',
              stripe_error_type: stripeError.type || null,
              payment_source: paymentSource,
              wallet_type: walletType,
            });
            const stripeMessage = stripeError.message || 'Failed to confirm payment method.';
            setError(stripeMessage);
            failWalletPayment(stripeMessage);
            setProcessing(false);
            return;
          }

          const confirmedPaymentIntent = paymentIntent || {
            id: setupData.paymentIntentId,
            status: setupData.paymentIntentStatus || setupData.status || 'processing',
          };

          if (
            isBankPayment &&
            (confirmedPaymentIntent.status === 'requires_action' ||
              confirmedPaymentIntent.next_action?.type === 'verify_with_microdeposits')
          ) {
            trackCheckoutDiagnostic('stripe_confirm_failed', {
              transaction_id: checkoutTransactionId,
              intent_type: 'payment_intent',
              payment_intent_status: confirmedPaymentIntent.status,
              blocked_reason: 'bank_verification_required',
              payment_source: paymentSource,
              wallet_type: walletType,
            });
            setError(
              'Bank account verification is needed before checkout can finish. Use card for instant access.',
            );
            setProcessing(false);
            return;
          }

          const acceptableStatuses = new Set(['succeeded', 'processing', 'requires_capture']);
          if (
            !confirmedPaymentIntent?.id ||
            !acceptableStatuses.has(confirmedPaymentIntent.status)
          ) {
            trackCheckoutDiagnostic('stripe_confirm_failed', {
              transaction_id: checkoutTransactionId,
              intent_type: 'payment_intent',
              payment_intent_status: confirmedPaymentIntent?.status || null,
              blocked_reason: 'unexpected_payment_intent_status',
              payment_source: paymentSource,
              wallet_type: walletType,
            });
            const paymentStatusMessage = 'Payment confirmation failed. Please try again.';
            setError(paymentStatusMessage);
            failWalletPayment(paymentStatusMessage);
            setProcessing(false);
            return;
          }

          trackCheckoutDiagnostic('stripe_confirm_succeeded', {
            transaction_id: checkoutTransactionId,
            intent_type: 'payment_intent',
            payment_intent_status: confirmedPaymentIntent.status,
            payment_source: paymentSource,
            wallet_type: walletType,
          });

          if (isWalletPayment) {
            completeWalletPayment('success');
            trackCheckoutDiagnostic('wallet_confirm_succeeded', {
              transaction_id: checkoutTransactionId,
              wallet_type: walletType,
              intent_type: 'payment_intent',
            });
          }

          const conversion = await convertLeadToTrial(
            {
              subscriptionId: setupData.subscriptionId,
              paymentIntentId: setupData.paymentIntentId,
              paymentIntentClientSecret: setupData.clientSecret,
              customerId: setupData.customerId,
              email: contactData.email,
              customerName: contactData.name,
              phoneNumber: contactData.phone,
              terms: termsPayload,
              ...(smsConsentPayload ? { smsConsent: smsConsentPayload } : {}),
              productSlugs: [productSlug || 'starter-plan'],
              ...salesSignupV4Attribution,
              cartMetadata: {
                productSlug: productSlug || 'starter-plan',
                experiment_variant: experimentVariant,
                ...checkoutExperienceEventProps,
                ...checkoutMonetaryEventProps,
                ...salesPageExperimentParams,
                ...salesSignupV4Attribution,
                billing_period: billingPeriod,
                stripe_mode: stripeMode,
                payment_source: paymentSource,
                wallet_type: walletType,
              },
              items: [
                {
                  productSlug: productSlug || 'starter-plan',
                  planKey: planKey, // Pass planKey for correct ProductPlan selection
                  quantity: 1,
                },
              ],
              // BUGFIX: Pass promo code to subscription creation for discount to be applied
              // Previously promoCode was sent to create-payment-intent but not to convert-lead-to-trial
              ...(effectiveCoupon ? { promoCode: effectiveCoupon } : {}),
              ...affiliateFields,
            },
            { apiUrl: API_URL, authToken: token },
          );

          await maybeConfirmInvoicePayment(conversion);
          pushEvent({
            event: ANALYTICS_EVENTS.PAYMENT_SUCCESS,
            transaction_id: conversion.subscriptionId || confirmedPaymentIntent.id,
            value: checkoutAnalyticsValueUsd,
            currency: 'USD',
            plan_type: productSlug || 'starter-plan',
            source: location.pathname,
            userId,
            user_type: isAuthenticated ? 'registered' : 'guest',
            customerId: conversion.customerId,
            paymentMethodId: confirmedPaymentIntent.payment_method || setupData.paymentMethodId,
            lead_transaction_id: leadTransactionId,
            experiment_variant: experimentVariant,
            experiment_name: checkoutExperimentName,
            ...checkoutExperienceEventProps,
            ...checkoutMonetaryEventProps,
            checkout_payment_result: 'initial_payment_collected',
            payment_source: paymentSource,
            wallet_type: walletType,
            how_found_us: contactData.howFoundUs || null,
            how_found_us_details: contactData.howFoundUsOtherText?.trim() || null,
            ...salesPageExperimentParams,
            ...salesSignupV4Attribution,
            ...utmData,
          });

          try {
            refreshPaymentStatus();
          } catch (refreshError) {
            console.warn('[StarterCheckoutPage] Failed to refresh payment status:', refreshError);
          }

          await attemptAutoLoginFlow(conversion, {
            paymentIntentId: confirmedPaymentIntent.id,
            paymentIntentClientSecret: setupData.clientSecret,
          });
          return;
        }

        if (!isWalletPayment && !isBankPayment) {
          const { error: submitError } = await elements.submit();
          if (submitError) {
            trackCheckoutDiagnostic('stripe_elements_submit_failed', {
              transaction_id: checkoutTransactionId,
              stripe_error_type: submitError.type || null,
              payment_source: paymentSource,
              wallet_type: walletType,
            });
            setError(submitError.message || 'Failed to submit payment details.');
            setProcessing(false);
            return;
          }
        }

        trackCheckoutDiagnostic('stripe_confirm_started', {
          transaction_id: checkoutTransactionId,
          intent_type: 'setup_intent',
          payment_source: paymentSource,
          wallet_type: walletType,
        });

        let stripeError = null;
        let setupIntent = null;
        if (isBankPayment && !false) {
          if (
            typeof stripe.collectBankAccountForSetup !== 'function' ||
            typeof stripe.confirmUsBankAccountSetup !== 'function'
          ) {
            trackCheckoutDiagnostic('stripe_confirm_failed', {
              transaction_id: checkoutTransactionId,
              intent_type: 'setup_intent',
              stripe_error_type: 'bank_api_unavailable',
              payment_source: paymentSource,
              wallet_type: walletType,
            });
            const bankUnavailableMessage =
              'Bank payment is not available in this browser. Please use card instead.';
            setError(bankUnavailableMessage);
            setProcessing(false);
            return;
          }

          trackCheckoutDiagnostic('stripe_bank_account_collection_started', {
            transaction_id: checkoutTransactionId,
            intent_type: 'setup_intent',
            payment_source: paymentSource,
          });

          const { error: bankCollectionError, setupIntent: collectedSetupIntent } =
            await stripe.collectBankAccountForSetup(bankCollectionOptions);

          if (bankCollectionError) {
            trackCheckoutDiagnostic('stripe_confirm_failed', {
              transaction_id: checkoutTransactionId,
              intent_type: 'setup_intent',
              stripe_error_type: bankCollectionError.type || 'bank_collection_failed',
              payment_source: paymentSource,
              wallet_type: walletType,
            });
            setError(
              bankCollectionError.message ||
                'Bank connection failed. Please try again or use card.',
            );
            setProcessing(false);
            return;
          }

          if (!collectedSetupIntent || collectedSetupIntent.status === 'requires_payment_method') {
            trackCheckoutDiagnostic('checkout_submit_blocked', {
              transaction_id: checkoutTransactionId,
              blocked_reason: 'bank_connection_cancelled',
              payment_source: paymentSource,
            });
            setError('Bank connection was cancelled. Choose bank again or use card.');
            setProcessing(false);
            return;
          }

          if (collectedSetupIntent.status === 'requires_confirmation') {
            const bankConfirmResult = await stripe.confirmUsBankAccountSetup(
              setupData.clientSecret,
            );
            stripeError = bankConfirmResult.error || null;
            setupIntent = bankConfirmResult.setupIntent || null;
          } else {
            setupIntent = collectedSetupIntent;
          }
        } else {
          const confirmResult = await confirmSetupWithClient(
            stripe,
            isElementsWalletPayment ? elements : isWalletPayment ? null : elements,
            {
              clientSecret: setupData.clientSecret,
              confirmParams: isWalletPayment
                ? walletConfirmParams
                : {
                    return_url: `${window.location.origin}/set-password?email=${encodeURIComponent(
                      contactData.email,
                    )}&name=${encodeURIComponent(contactData.name)}&redirectTo=onboarding&source_flow=landing_checkout_setup_redirect`,
                  },
              redirect: 'if_required',
              ...{},
            },
          );
          stripeError = confirmResult.error || null;
          setupIntent = confirmResult.setupIntent || null;
        }

        if (stripeError) {
          trackCheckoutDiagnostic('stripe_confirm_failed', {
            transaction_id: checkoutTransactionId,
            intent_type: 'setup_intent',
            stripe_error_type: stripeError.type || null,
            payment_source: paymentSource,
            wallet_type: walletType,
          });
          const stripeMessage = stripeError.message || 'Failed to confirm payment method.';
          setError(stripeMessage);
          failWalletPayment(stripeMessage);
          setProcessing(false);
          return;
        }

        if (!setupIntent || setupIntent.status !== 'succeeded') {
          const bankVerificationRequired =
            isBankPayment &&
            (setupIntent?.status === 'requires_action' ||
              setupIntent?.next_action?.type === 'verify_with_microdeposits');
          trackCheckoutDiagnostic('stripe_confirm_failed', {
            transaction_id: checkoutTransactionId,
            intent_type: 'setup_intent',
            setup_intent_status: setupIntent?.status || null,
            blocked_reason: bankVerificationRequired
              ? 'bank_verification_required'
              : 'unexpected_setup_intent_status',
            payment_source: paymentSource,
            wallet_type: walletType,
          });
          const setupStatusMessage = bankVerificationRequired
            ? 'Bank account verification is needed before checkout can finish. Use card for instant access.'
            : 'Payment method validation failed. Please try again.';
          setError(setupStatusMessage);
          failWalletPayment(setupStatusMessage);
          setProcessing(false);
          return;
        }

        trackCheckoutDiagnostic('stripe_confirm_succeeded', {
          transaction_id: checkoutTransactionId,
          intent_type: 'setup_intent',
          setup_intent_status: setupIntent.status,
          payment_source: paymentSource,
          wallet_type: walletType,
        });

        if (isWalletPayment) {
          completeWalletPayment('success');
          trackCheckoutDiagnostic('wallet_confirm_succeeded', {
            transaction_id: checkoutTransactionId,
            wallet_type: walletType,
            intent_type: 'setup_intent',
          });
        }

        const conversion = await convertLeadToTrial(
          {
            setupIntentId: setupIntent.id,
            setupIntentClientSecret: setupData.clientSecret,
            customerId: setupIntent.customer || setupData.customerId,
            email: contactData.email,
            customerName: contactData.name,
            phoneNumber: contactData.phone,
            terms: termsPayload,
            ...(smsConsentPayload ? { smsConsent: smsConsentPayload } : {}),
            productSlugs: [productSlug || 'starter-plan'],
            ...(isBankPayment ? { bankVerificationMethod } : {}),
            ...salesSignupV4Attribution,
            cartMetadata: {
              productSlug: productSlug || 'starter-plan',
              experiment_variant: experimentVariant,
              ...checkoutExperienceEventProps,
              ...checkoutMonetaryEventProps,
              ...salesPageExperimentParams,
              ...salesSignupV4Attribution,
              billing_period: billingPeriod,
              stripe_mode: stripeMode,
              payment_source: paymentSource,
              wallet_type: walletType,
              bank_verification_method: isBankPayment ? bankVerificationMethod : undefined,
            },
            items: [
              {
                productSlug: productSlug || 'starter-plan',
                planKey: planKey, // Pass planKey for correct ProductPlan selection
                quantity: 1,
              },
            ],
            // BUGFIX: Pass promo code to subscription creation for discount to be applied
            ...(effectiveCoupon ? { promoCode: effectiveCoupon } : {}),
            ...affiliateFields,
          },
          { apiUrl: API_URL, authToken: token },
        );

        await maybeConfirmInvoicePayment(conversion);
        pushEvent({
          event: ANALYTICS_EVENTS.PAYMENT_SUCCESS,
          transaction_id: conversion.subscriptionId || setupIntent.id,
          value: 0,
          currency: 'USD',
          plan_type: productSlug || 'starter-plan',
          source: location.pathname,
          userId,
          user_type: isAuthenticated ? 'registered' : 'guest',
          customerId: conversion.customerId,
          paymentMethodId: setupIntent.payment_method,
          lead_transaction_id: leadTransactionId,
          experiment_variant: experimentVariant,
          experiment_name: checkoutExperimentName,
          ...checkoutExperienceEventProps,
          ...checkoutMonetaryEventProps,
          checkout_payment_result: 'payment_method_setup_completed',
          payment_source: paymentSource,
          wallet_type: walletType,
          how_found_us: contactData.howFoundUs || null,
          how_found_us_details: contactData.howFoundUsOtherText?.trim() || null,
          ...salesPageExperimentParams,
          ...salesSignupV4Attribution,
          ...utmData,
        });

        try {
          refreshPaymentStatus();
        } catch (refreshError) {
          console.warn('[StarterCheckoutPage] Failed to refresh payment status:', refreshError);
        }

        await attemptAutoLoginFlow(conversion, {
          setupIntentId: setupIntent.id,
          setupIntentClientSecret: setupData.clientSecret,
        });
      } catch (submitError) {
        console.error('[StarterCheckoutPage] Error processing payment:', submitError);

        trackCheckoutDiagnostic('checkout_unhandled_error', {
          error_message: submitError?.message || 'unknown_error',
          payment_source: paymentSource,
          wallet_type: walletType,
          ...{},
        });

        const fallbackMessage = 'An unexpected error occurred. Please try again.';
        setError(fallbackMessage);
        failWalletPayment(fallbackMessage);
        setProcessing(false);
      }
    },
    [
      activePrompt,
      checkoutExperienceEventProps,
      checkoutExperimentName,
      checkoutMonetaryEventProps,
      checkoutAmountDueTodayCents,
      false,
      contactComplete,
      contactData.email,
      contactData.howFoundUs,
      contactData.howFoundUsOtherText,
      contactData.name,
      contactData.phone,
      elements,
      getApiToken,
      isAuthenticated,
      isValidatingCoupon,
      leadTransactionId,
      location.pathname,
      false,
      bankVerificationMethod,
      paymentElementReady,
      paymentMethodAvailabilityChecked,
      paymentMethodAvailabilityError,
      productSlug,
      processing,
      selectedPaymentMethod,
      smsConsentAccepted,
      stripe,
      termsAccepted,
      utmData,
      user,
      refreshPaymentStatus,
      salesPageExperimentParams,
      salesSignupV4Attribution,
      variantParam,
      attemptAutoLoginFlow,
      experimentVariant,
      planKey,
      billingPeriod,
      effectiveCoupon,
      maybeConfirmInvoicePayment,
      attributionParams,
      stripeMode,
      trackCheckoutDiagnostic,
      howFoundUsComplete,
      selectedSavedPaymentMethodId,
    ],
  );

  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  useEffect(() => {
    if (!pendingBuyerSubmitRef.current || processing) return;
    if (getValidationState().validation_message) return;
    pendingBuyerSubmitRef.current = false;
    void handleSubmitRef.current?.();
  }, [
    contactComplete,
    elements,
    getValidationState,
    isValidatingCoupon,
    paymentElementReady,
    processing,
    stripe,
    termsAccepted,
  ]);

  const activePaymentReady = paymentElementReady;
  const isSubmitDisabled =
    processing ||
    isValidatingCoupon ||
    elementsUpdating ||
    !termsAccepted ||
    !contactComplete ||
    !activePaymentReady;

  const configuredWalletAmountCents = configuredPlanAmountCents;

  const walletRequestAmountCents = useMemo(
    () =>
      Math.max(
        0,
        appliedCoupon?.discountedAmount ??
          (stripeMode === 'setup' ? 0 : configuredWalletAmountCents),
      ),
    [appliedCoupon?.discountedAmount, configuredWalletAmountCents, stripeMode],
  );

  const walletPaymentRequestOptions = useMemo(
    () => ({
      country: 'US',
      currency: 'usd',
      total: {
        label: stripeMode === 'setup' ? `${planDisplayName} 7-day access` : planDisplayName,
        amount: walletRequestAmountCents,
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
    }),
    [planDisplayName, stripeMode, walletRequestAmountCents],
  );

  const handleWalletPaymentClick = useCallback(
    (walletType) => {
      trackCheckoutDiagnostic('wallet_clicked', {
        wallet_type: walletType,
        contact_complete: Boolean(contactComplete),
        terms_accepted: Boolean(termsAccepted),
      });

      setError(
        'Apple Pay or Google Pay is not available in this browser. Please use card instead.',
      );
      setSelectedPaymentMethod('card');
      trackCheckoutDiagnostic('wallet_click_blocked', {
        blocked_reason: 'wallet_not_available',
        wallet_type: walletType,
      });
      return;
    },
    [
      contactComplete,
      termsAccepted,
      trackCheckoutDiagnostic,
      walletPaymentRequest,
      walletPaymentRequestOptions.total,
    ],
  );

  useEffect(() => {
    googlePayWalletClickRef.current = handleWalletPaymentClick;
  }, [handleWalletPaymentClick]);

  useEffect(() => {
    setGooglePayButtonReady(false);
    setGooglePayButtonFailed(false);
    return undefined;
  }, [availableWalletMethods, checkoutTheme, false]);

  const paymentElementOptions = useMemo(
    () => ({
      layout: { type: 'tabs', defaultCollapsed: false },
      wallets: {
        applePay: process.env.NODE_ENV === 'production' ? 'auto' : 'never',
        googlePay: process.env.NODE_ENV === 'production' ? 'auto' : 'never',
      },
      // Hide Stripe's internal terms text since we have our own Terms of Service checkbox
      // This prevents the "By providing your card..." text from being cut off on mobile
      terms: {
        card: 'never',
        applePay: 'never',
        googlePay: 'never',
      },
      defaultValues: {
        billingDetails: {
          name: contactData.name || undefined,
          email: contactData.email || undefined,
          phone: contactData.phone || undefined,
        },
      },
    }),
    [contactData.email, contactData.name, contactData.phone],
  );

  return (
    <form
      className={`landing-checkout-form checkout-form-card ${error?.toLowerCase().includes('already subscribed') ? 'checkout-form-disabled' : ''}`}
      onSubmit={handleSubmit}
    >
      <CheckoutContactFields
        value={contactData}
        onChange={handleContactChange}
        disabled={processing}
        autoFocus
        collapseWhenComplete={contactWasPrefilledOnLoad}
        smsConsentId="landing-checkout-sms-consent"
        smsConsentAccepted={smsConsentAccepted}
        onSmsConsentChange={handleSmsConsentChange}
      />

      {/* Billing Period Toggle - shown only when the selected product has a configured annual plan. */}
      {annualPlanKey && (
        <div className="billing-toggle-section">
          <div className="billing-toggle-container">
            <button
              type="button"
              className={`billing-toggle-btn ${billingPeriod === 'monthly' ? 'active' : ''}`}
              onClick={() => handleBillingPeriodChange('monthly')}
              disabled={processing}
            >
              Monthly
            </button>
            <button
              type="button"
              className={`billing-toggle-btn ${billingPeriod === 'annual' ? 'active' : ''}`}
              onClick={() => handleBillingPeriodChange('annual')}
              disabled={processing}
            >
              Annual
              {annualSavingsText && (
                <span className="billing-savings-badge">{annualSavingsText}</span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Show trial callout for trial variants, discount banner for discount variant, or price callout for no_trial */}
      {showUrgencyTimer ? (
        <div className="checkout-urgency-banner">
          <div className="urgency-pricing">
            <span className="urgency-original-price">Was {originalPrice}</span>
            <span className="urgency-current-price">{priceText}</span>
            <span className="urgency-badge">Launch discount applied</span>
          </div>
          {/* Show monthly equivalent when annual is selected */}
          {isAnnual && annualMonthlyEquivalent && (
            <div className="urgency-monthly-equivalent">(Just {annualMonthlyEquivalent})</div>
          )}
          <div className="urgency-message">Price shown reflects the current launch discount.</div>
        </div>
      ) : (
        <CheckoutTrialCallout
          trialDays={trialDays}
          priceText={priceText}
          annualMonthlyEquivalent={isAnnual ? annualMonthlyEquivalent : null}
        />
      )}

      <section className="checkout-next-steps-panel" aria-label="What happens after payment">
        <h2>{checkoutNextStep.title}</h2>
        <div className="checkout-next-steps-panel__item">
          <FaCheckCircle className="checkout-next-steps-panel__icon" />
          <span>{checkoutNextStep.primary}</span>
        </div>
        <div className="checkout-next-steps-panel__item">
          <FaCheckCircle className="checkout-next-steps-panel__icon" />
          <span>{checkoutNextStep.secondary}</span>
        </div>
        <p>BotSpot helps automate your rules. It is software, not investment advice.</p>
      </section>

      <CheckoutPaymentContainer>
        <StripeErrorBoundary>
          <PaymentElement
            options={paymentElementOptions}
            onChange={(event) => {
              setPaymentElementReady(event.complete);
              if (event.complete && !paymentElementCompleteTrackedRef.current) {
                paymentElementCompleteTrackedRef.current = true;
                trackCheckoutDiagnostic('stripe_payment_element_completed');
              }
            }}
            onReady={() => {
              setPaymentElementReady(true);
              if (!paymentElementReadyTrackedRef.current) {
                paymentElementReadyTrackedRef.current = true;
                trackCheckoutDiagnostic('stripe_elements_ready');
              }
            }}
            onLoadError={(paymentError) => {
              console.error('[StarterCheckoutPage] PaymentElement load error:', paymentError);
              trackCheckoutDiagnostic('stripe_elements_load_error', {
                error_message: paymentError?.message || 'unknown_error',
              });
              setError('Payment form failed to load. Please refresh and try again.');
            }}
          />
        </StripeErrorBoundary>
      </CheckoutPaymentContainer>

      {/* Promo code input section */}
      <div className="coupon-code-section">
        {linkCouponNotice && !appliedCoupon ? (
          <div
            className="coupon-code-form__error"
            role="alert"
            data-testid="checkout-link-coupon-notice"
          >
            {`The code ${linkCouponNotice.code} in your link ${linkCouponNotice.reason}. You can check out at the price shown or try another code.`}
          </div>
        ) : null}
        {appliedCoupon ? (
          <div className="coupon-code-applied">
            <div className="coupon-code-applied__info">
              <FaTag className="coupon-code-applied__icon" />
              <div className="coupon-code-applied__details">
                <span className="coupon-code-applied__code">{appliedCoupon.code}</span>
                {appliedCoupon.description && (
                  <span className="coupon-code-applied__description">
                    {appliedCoupon.description}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              className="coupon-code-applied__remove"
              onClick={handleRemoveCoupon}
              disabled={processing || isValidatingCoupon}
              aria-label="Remove coupon"
            >
              <FaTimes />
            </button>
          </div>
        ) : (
          <CouponCodeInput
            onApply={handleApplyCoupon}
            onClearValidationError={() => setCouponValidationError('')}
            isValidating={isValidatingCoupon}
            validationError={couponValidationError}
            disabled={processing}
          />
        )}
      </div>

      <div className="checkout-security-badges">
        <span>
          <FaShieldAlt className="badge-icon" />
          SSL Secured
        </span>
        <span>
          <FaShieldAlt className="badge-icon" />
          Encrypted
        </span>
      </div>

      {error ? (
        <div className="checkout-error-banner" role="alert">
          {error.toLowerCase().includes('already subscribed') ? (
            <>
              You're already subscribed! No need to pay again.
              <Link to="/ai-agent" className="checkout-error-link">
                Go to AI Agent →
              </Link>
            </>
          ) : (
            error
          )}
        </div>
      ) : null}

      <label className={`terms-checkbox-group ${termsError ? 'error' : ''}`}>
        <input
          type="checkbox"
          className="terms-checkbox-input"
          id="landing-checkout-terms"
          aria-label={`I agree to the Terms of Service and understand this will start my ${termsText}.`}
          checked={termsAccepted}
          onChange={(event) => {
            setTermsAccepted(event.target.checked);
            if (event.target.checked) {
              setTermsError(false);
              // Clear error if terms was the issue
              if (isTermsAcceptanceErrorMessage(error)) {
                setError('');
              }
            }
          }}
          disabled={processing}
          required
        />
        <span className="terms-text">
          I agree to the{' '}
          <a href="/terms" className="terms-link" onClick={(e) => e.stopPropagation()}>
            Terms of Service
          </a>{' '}
          and understand this will start my {termsText}.
        </span>
      </label>
      {termsError && <div className="terms-error-text">Please accept the Terms of Service</div>}

      {!hasPrefilledHowFoundUs && (
        <HowFoundUsDropdown
          value={contactData.howFoundUs}
          otherText={contactData.howFoundUsOtherText}
          onChange={(val) => handleContactChange('howFoundUs', val)}
          onOtherTextChange={(val) => handleContactChange('howFoundUsOtherText', val)}
          disabled={processing}
        />
      )}

      {/* Price summary when coupon is applied */}
      {appliedCoupon && appliedCoupon.discountedAmount != null && (
        <div className="landing-checkout-price-summary">
          <div className="landing-checkout-price-summary__row">
            <span>{planDisplayName}</span>
            <span className="landing-checkout-price-summary__original">
              ${((appliedCoupon.originalAmount || 9900) / 100).toFixed(2)}/month
            </span>
          </div>
          <div className="landing-checkout-price-summary__row landing-checkout-price-summary__row--discount">
            <span>Discount ({appliedCoupon.code})</span>
            <span>
              -$
              {(
                ((appliedCoupon.originalAmount || 9900) - appliedCoupon.discountedAmount) /
                100
              ).toFixed(2)}
            </span>
          </div>
          <div className="landing-checkout-price-summary__row landing-checkout-price-summary__row--total">
            <span>You pay today</span>
            <span>${(appliedCoupon.discountedAmount / 100).toFixed(2)}</span>
          </div>
          {appliedCoupon.discountedAmount < (appliedCoupon.originalAmount || 9900) && (
            <div className="landing-checkout-price-summary__after">
              Then ${((appliedCoupon.originalAmount || 9900) / 100).toFixed(2)}
              /month after
            </div>
          )}
        </div>
      )}

      <div className="checkout-button-wrapper">
        <button
          ref={buttonRef}
          type="submit"
          className={`checkout-cta-button ${processing || isValidatingCoupon ? 'disabled' : ''}`}
          onClick={handleButtonClick}
          onMouseEnter={() => isSubmitDisabled && setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          disabled={processing || isValidatingCoupon}
        >
          {processing
            ? 'Processing...'
            : isValidatingCoupon
              ? 'Applying coupon...'
              : appliedCoupon?.discountedAmount === 0
                ? 'Start for $0'
                : ctaText}
        </button>
        <Overlay target={buttonRef.current} show={showTooltip} placement="top">
          {(props) => (
            <Tooltip id="checkout-validation-tooltip" className="checkout-tooltip" {...props}>
              {getValidationMessage()}
            </Tooltip>
          )}
        </Overlay>
      </div>

      <div className="checkout-reassurance-list">
        <span>
          <FaCheckCircle className="reassurance-icon" /> Instant account access
        </span>
        <span>
          <FaCheckCircle className="reassurance-icon" /> Manage your subscription from your account
        </span>
      </div>
    </form>
  );
}

/**
 * PlanCheckoutPage - Shared checkout page for Starter and Light plan routes.
 *
 * STRIPE MODE ARCHITECTURE:
 * ========================
 * This component determines the Stripe Elements mode based on whether the user will
 * have a trial period. This is critical because Stripe Elements must be initialized
 * with the correct mode BEFORE collecting payment details:
 *
 * - 'setup' mode: For trial subscriptions. Collects payment method for future charges.
 *   Backend returns SetupIntent (seti_...). Frontend calls confirmSetup().
 *
 * - 'payment' mode: For immediate charges (no trial). Processes payment immediately.
 *   Backend returns PaymentIntent (pi_...). Frontend calls confirmPayment().
 *
 * The mode cannot be changed after Elements initialization, so we:
 * 1. Lift billingPeriod state here (not in form) to compute mode before Elements mount
 * 2. Use a dynamic key on Elements to force re-mount when mode changes
 *
 * MODE DETERMINATION LOGIC:
 * -------------------------
 * effectiveTrialDays = isAnnual ? annualTrialDays : trialDays
 * stripeMode = effectiveTrialDays > 0 ? 'setup' : 'payment'
 *
 * Starter and Light use payment mode; a validated zero-due coupon uses setup mode.
 */
function PlanCheckoutPage() {
  const location = useLocation();

  const checkoutUser = useUser();
  const { userProfile } = checkoutUser;
  const [themeMode] = useThemeMode();

  const queryParams = new URLSearchParams(location.search);
  // Path-based default: /checkout/light-plan → always Light (user explicitly chose Light
  // on the pricing page). /checkout/starter-plan with no explicit product is the
  // sales/marketplace paywall surface and is eligible for the Light-vs-Starter price test.
  const isLightPathname = location.pathname === '/checkout/light-plan';
  const pathnameSlug = isLightPathname ? 'light-plan' : 'starter-plan';
  const explicitProductSlug = queryParams.get('product');
  const productSlugFromUrl = explicitProductSlug || pathnameSlug;
  const testVariantParam = queryParams.get('testVariant');

  const checkoutThemeParam = queryParams.get('checkoutTheme');
  const billingPeriodParam = queryParams.get('billingPeriod');
  const allowsPreviewOverrides = isPreviewExperimentOverrideHost();
  const hasTestVariantOverride =
    allowsPreviewOverrides && Boolean(testVariantParam && VARIANT_CONFIG[testVariantParam]);

  const hasCheckoutThemeOverride =
    allowsPreviewOverrides && CHECKOUT_THEME_VARIANTS.includes(checkoutThemeParam);
  const hasPricingPropOverride = Boolean(null);
  const isStarterPlanExperimentSurface =
    !isLightPathname && !explicitProductSlug && !hasTestVariantOverride && !hasPricingPropOverride;

  const isExplicitBillingPeriod =
    billingPeriodParam === 'monthly' || billingPeriodParam === 'annual';
  const [billingPeriodOverride, setBillingPeriod] = useState(
    isExplicitBillingPeriod ? billingPeriodParam : null,
  );
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [autoCouponValidation, setAutoCouponValidation] = useState({
    key: null,
    loading: false,
    notice: null,
  });

  // Light vs Starter Checkout A/B test (the successor to STARTER_PRICING_ABC).
  // This test runs on /checkout/starter-plan ONLY. control = Starter $99→$49,
  // test = Light $49→$24. Same urgency UX, same 50%-off framing, pure price swap.
  const lightCheckoutExperiment = ACTIVE_EXPERIMENTS.LIGHT_VS_STARTER_CHECKOUT;
  const { variant: lightCheckoutVariant, isLoading: experimentLoading } = useABTest(
    lightCheckoutExperiment.name,
    lightCheckoutExperiment.featureFlag,
    'control',
    {
      page: 'landing_checkout',
      product_slug: productSlugFromUrl,
      validVariants: PRICE_EXPERIMENT_VARIANTS,
      normalizeControlExposure: true,
      skipFeatureFlagEvaluation: !isStarterPlanExperimentSurface,
      forcedVariant: 'control',
    },
  );

  // A code in the link keeps a new visitor out of the checkout test (see the hook).
  const hasLinkPromoCode = ['coupon', 'promoCode', 'promo'].some((key) =>
    Boolean(new URLSearchParams(location.search).get(key)),
  );
  const checkoutExperienceExperiment = useCheckoutExperienceExperiment('plan_checkout', undefined, {
    excludeNewVisitors: hasLinkPromoCode,
  });

  // Allow URL parameter override for testing: ?testVariant=discounttest-2|light
  // This only works on approved preview hosts for QA purposes.

  // Resolve which variant config to use. Priority order:
  //   1. Pathname /checkout/light-plan → always Light (user clicked Light CTA)
  //   2. ?product=starter-plan or ?product=light-plan → honor explicit pricing-page choice
  //   3. ?testVariant=... URL override (QA / dev only)
  //   4. light-vs-starter-checkout flag → 'test' means Light, 'control' means Starter
  const experimentVariant = useMemo(() => {
    if (isLightPathname) {
      return 'light';
    }
    if (explicitProductSlug === 'light-plan') {
      return 'light';
    }
    if (explicitProductSlug === 'starter-plan') {
      return 'discounttest-2';
    }
    if (hasTestVariantOverride) {
      if (SHOULD_LOG_CHECKOUT_DEBUG) {
        console.log(`🧪 [A/B Test Override] Using testVariant=${testVariantParam} from URL`);
      }
      return testVariantParam;
    }
    // On /checkout/starter-plan the flag decides: test variant swaps in Light.
    if (lightCheckoutVariant === 'test') {
      return 'light';
    }
    // Default / control: Starter 50%-off (discounttest-2, the winner of the previous test).
    return 'discounttest-2';
  }, [
    explicitProductSlug,
    hasTestVariantOverride,
    isLightPathname,
    lightCheckoutVariant,
    testVariantParam,
  ]);

  const savedMethods = EMPTY_SAVED_PAYMENT_METHODS;

  const checkoutTheme = hasCheckoutThemeOverride ? checkoutThemeParam : applyThemeMode(themeMode);

  // When the A/B test assigns a user on /checkout/starter-plan to the 'light' variant,
  // we need to bill them through the light-plan product, not starter-plan. The Stripe
  // PaymentIntent backend looks up Product + ProductPlan by this slug, so getting this
  // wrong would bill Starter $49 while displaying Light $24 UX — which would be a bug.
  const productSlug = useMemo(() => {
    if (experimentVariant === 'light') {
      return 'light-plan';
    }
    return productSlugFromUrl;
  }, [experimentVariant, productSlugFromUrl]);

  // Get variant config based on experiment variant
  const baseVariantConfig = useMemo(() => getVariantConfig(experimentVariant), [experimentVariant]);
  const variantConfig = useMemo(() => baseVariantConfig, [baseVariantConfig]);
  const canUseAnnualBilling = Boolean(variantConfig.annualPlanKey);
  const requestedBillingPeriod =
    variantConfig.fixedBillingPeriod === 'annual'
      ? 'annual'
      : variantConfig.fixedBillingPeriod
        ? 'monthly'
        : billingPeriodOverride || variantConfig.defaultBillingPeriod || 'monthly';
  const billingPeriod =
    requestedBillingPeriod === 'annual' && !canUseAnnualBilling
      ? 'monthly'
      : requestedBillingPeriod;

  const selectedPlanKey = useMemo(() => {
    if (billingPeriod === 'annual') {
      return variantConfig.annualPlanKey;
    }
    return variantConfig.monthlyPlanKey || variantConfig.planKey;
  }, [billingPeriod, variantConfig]);

  const urlCouponCode =
    queryParams.get('coupon') || queryParams.get('promoCode') || queryParams.get('promo') || '';

  const autoCouponValidationKey = urlCouponCode
    ? `${productSlug}:${selectedPlanKey || 'none'}:${urlCouponCode.toUpperCase()}`
    : null;

  useEffect(() => {
    if (!autoCouponValidationKey) return;

    let isCancelled = false;
    setAutoCouponValidation({ key: autoCouponValidationKey, loading: true });

    const validateUrlCoupon = async () => {
      try {
        const catalog = await validatePromoCode(urlCouponCode, null);
        const discountedPlan = findPlanDiscountedByCode(
          catalog,
          productSlug,
          selectedPlanKey,
          urlCouponCode,
        );

        if (!isCancelled) {
          setAppliedCoupon(
            discountedPlan ? couponStateFromPlan(urlCouponCode, discountedPlan) : null,
          );
          // A dead link code must not vanish silently: say why the price is regular.
          setAutoCouponValidation({
            key: autoCouponValidationKey,
            loading: false,
            notice: discountedPlan
              ? null
              : {
                  code: urlCouponCode.toUpperCase(),
                  reason: linkCodeReason(catalog?.promotionCodeValidation?.status),
                },
          });
        }
      } catch (err) {
        if (!isCancelled) {
          setAppliedCoupon(null);
          setAutoCouponValidation({
            key: autoCouponValidationKey,
            loading: false,
            notice: {
              code: urlCouponCode.toUpperCase(),
              reason: linkCodeReason(null),
            },
          });
        }
      }
    };

    validateUrlCoupon();

    return () => {
      isCancelled = true;
    };
  }, [autoCouponValidationKey, productSlug, selectedPlanKey, urlCouponCode]);

  // Compute effective trial days based on billing period
  // Annual billing typically has no trial (pay upfront for full year)
  const effectiveTrialDays = useMemo(() => {
    const isAnnual = billingPeriod === 'annual';
    return isAnnual ? variantConfig.annualTrialDays || 0 : variantConfig.trialDays || 0;
  }, [billingPeriod, variantConfig]);

  // Determine Stripe Elements mode based on trial days and $0-due coupons.
  // This MUST be computed before Elements initialization
  // - 'setup' mode: For trials - collect payment method for future charges (SetupIntent)
  // - 'setup' mode: For fully discounted first invoices - collect a payment method now
  // - 'payment' mode: For immediate charges (PaymentIntent)
  const zeroDueTodayFromCoupon = appliedCoupon?.discountedAmount === 0;
  const stripeMode = effectiveTrialDays > 0 || zeroDueTodayFromCoupon ? 'setup' : 'payment';
  const shouldSetupFutureUsage = variantConfig.setupFutureUsage !== false;

  useEffect(() => {
    if (!SHOULD_LOG_CHECKOUT_DEBUG) return;
    console.log(
      `[StarterCheckoutPage] Stripe mode: ${stripeMode}, effectiveTrialDays: ${effectiveTrialDays}, billingPeriod: ${billingPeriod}, variant: ${experimentVariant}, zeroDueTodayFromCoupon: ${zeroDueTodayFromCoupon}`,
    );
  }, [stripeMode, effectiveTrialDays, billingPeriod, experimentVariant, zeroDueTodayFromCoupon]);

  // Priority: URL params > user profile > empty string
  // For authenticated users, pre-fill from their profile
  const profileName =
    userProfile?.firstName && userProfile?.lastName
      ? `${userProfile.firstName} ${userProfile.lastName}`.trim()
      : userProfile?.firstName || '';
  const name = queryParams.get('name') || profileName || '';
  const email = queryParams.get('email') || userProfile?.email || '';
  const phone = queryParams.get('phone') || userProfile?.phone || '';
  const howFoundUs = queryParams.get('howFoundUs') || userProfile?.howFoundUs || '';
  const howFoundUsOtherText =
    queryParams.get('howFoundUsOtherText') ||
    queryParams.get('howFoundUsDetails') ||
    userProfile?.howFoundUsDetails ||
    '';

  const appearance = useMemo(() => {
    return {
      theme: checkoutTheme === 'light' ? 'stripe' : 'night',
      variables: {
        colorPrimary: '#ff6b35',
        // Note: Stripe only accepts HEX, rgb(), or hsl() - NOT rgba()
        colorBackground: checkoutTheme === 'light' ? '#ffffff' : '#1a1f2e',
        colorText: checkoutTheme === 'light' ? '#0f172a' : '#f0f0f0',
        colorDanger: '#ff5252',
        fontFamily: 'system-ui, sans-serif',
        borderRadius: '12px',
      },
    };
  }, [checkoutTheme]);

  // Dynamic Elements options based on computed stripe mode
  // CRITICAL: mode must match what backend will return (SetupIntent vs PaymentIntent)
  // For subscriptions in payment mode, backend creates PaymentIntent with setup_future_usage: 'off_session'
  // so we MUST tell Elements to expect this, otherwise Stripe throws a mismatch error
  //
  // Setup mode confirms a SetupIntent created with automatic_payment_methods.
  // Charge-now payment mode confirms a standalone automatic PaymentIntent.
  // Do not pin paymentMethodTypes. A pinned list cannot confirm that intent,
  // and Pay then sits until the five-minute ceiling.
  const elementsOptions = useMemo(
    () => ({
      mode: stripeMode,
      currency: 'usd',
      appearance,
      loader: 'auto',
      ...(stripeMode === 'payment'
        ? {
            amount:
              appliedCoupon?.discountedAmount ??
              getVariantAmountCents(variantConfig, billingPeriod),
            // Recurring plans should save the payment method for future invoices.
            ...(shouldSetupFutureUsage ? { setupFutureUsage: 'off_session' } : {}),
          }
        : {}),
    }),
    [appearance, stripeMode, appliedCoupon, billingPeriod, shouldSetupFutureUsage, variantConfig],
  );

  // Callback for billing period change - passed to child form
  const handleBillingPeriodChange = useCallback(
    (newPeriod) => {
      if (newPeriod === billingPeriod) return;

      setBillingPeriod(newPeriod);
    },
    [billingPeriod],
  );

  const initialUserInfo = useMemo(
    () => ({
      name,
      email,
      phone,
      howFoundUs,
      howFoundUsOtherText,
    }),
    [name, email, phone, howFoundUs, howFoundUsOtherText],
  );

  const isWaitingForUrlCoupon =
    Boolean(autoCouponValidationKey) &&
    (autoCouponValidation.loading || autoCouponValidation.key !== autoCouponValidationKey);
  const hasExplicitPricing =
    hasPricingPropOverride ||
    hasTestVariantOverride ||
    isLightPathname ||
    Boolean(explicitProductSlug);
  const isWaitingForPricingExperiment = experimentLoading && !hasExplicitPricing;
  const isWaitingForCheckoutExperienceExperiment = checkoutExperienceExperiment.isLoading;
  const [hostedPurchase, setHostedPurchase] = useState(null);
  // A new visitor with a link code is never enrolled (the hook excludes them),
  // so only buyers already in the hosted arm reach Stripe here.
  const isHostedCheckout =
    checkoutExperienceExperiment.variant === 'stripe_hosted' &&
    checkoutExperienceExperiment.enrolled;
  useEffect(() => {
    if (!isHostedCheckout && !checkoutExperienceExperiment.isLoading) {
      if (hostedPurchase) setHostedPurchase(null);
      return;
    }
    if (
      hostedPurchase ||
      !isHostedCheckout ||
      isWaitingForPricingExperiment ||
      isWaitingForCheckoutExperienceExperiment ||
      isWaitingForUrlCoupon
    )
      return;
    // Keep the resolved purchase and launcher mounted through background refreshes.
    setHostedPurchase({
      items: [{ productSlug, planKey: selectedPlanKey, quantity: 1 }],
      promoCode: appliedCoupon?.code || urlCouponCode || null,
      returnContext: 'signup_survey',
      sourceRoute: location.pathname,
      flowContext: queryParams.get('source') || 'plan_checkout',
      surface: 'plan_checkout',
      assignment: checkoutExperienceExperiment,
      getApiToken: checkoutUser.getApiToken,
    });
  }, [
    hostedPurchase,
    isHostedCheckout,
    checkoutExperienceExperiment,
    isWaitingForPricingExperiment,
    isWaitingForCheckoutExperienceExperiment,
    isWaitingForUrlCoupon,
    productSlug,
    selectedPlanKey,
    appliedCoupon?.code,
    urlCouponCode,
    location.pathname,
    queryParams,
    checkoutUser.getApiToken,
  ]);

  if (hostedPurchase && (isHostedCheckout || checkoutExperienceExperiment.isLoading))
    return (
      <HostedCheckoutLauncher
        {...hostedPurchase}
        assignment={
          checkoutExperienceExperiment.isLoading
            ? hostedPurchase.assignment
            : checkoutExperienceExperiment
        }
      />
    );

  // ProtectedRoute already holds checkout until auth resolves. The form can render
  // immediately and fill any still-empty contact fields when the profile arrives.
  // Keep the blocking loader only for data that changes the displayed plan/price.
  if (
    isWaitingForPricingExperiment ||
    isWaitingForCheckoutExperienceExperiment ||
    isWaitingForUrlCoupon ||
    isHostedCheckout
  ) {
    return (
      <div className="checkout-landing-page bsui-scope" data-bs-theme={checkoutTheme}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} className="text-center py-5">
              <p>Loading...</p>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (!stripeKey) {
    return (
      <div className="checkout-landing-page bsui-scope" data-bs-theme={checkoutTheme}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} className="text-center py-5">
              <h2>Configuration Error</h2>
              <p>Payment system is not properly configured. Please contact support.</p>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="checkout-landing-page bsui-scope" data-bs-theme={checkoutTheme}>
      <header className="landing-checkout-header">
        <h1>{variantConfig.headerText}</h1>
        <p className="header-subtitle">
          {variantConfig.subheaderText || 'Unlock the AI Agent in under two minutes'}
        </p>
      </header>

      <div className="trust-strip-wrapper">
        <TrustStrip />
      </div>

      <Container className="landing-checkout-container">
        <Row className="justify-content-center">
          <Col lg={7} md={10}>
            {/* Key changes when stripeMode changes to force Elements re-initialization
                This is necessary because Stripe Elements mode cannot be changed after init */}
            <CheckoutStripeElements
              key={`landing-checkout-${stripeMode}-${billingPeriod}-${appliedCoupon?.code || 'none'}-${appliedCoupon?.discountedAmount ?? 'full'}`}
              publishableKey={stripeKey}
              options={elementsOptions}
            >
              <StarterCheckoutForm
                productSlug={productSlug}
                initialUserInfo={initialUserInfo}
                variantConfig={variantConfig}
                experimentVariant={experimentVariant}

                billingPeriod={billingPeriod}
                onBillingPeriodChange={handleBillingPeriodChange}
                stripeMode={stripeMode}
                appliedCoupon={appliedCoupon}
                onAppliedCouponChange={setAppliedCoupon}
                savedMethods={savedMethods}
                checkoutUser={checkoutUser}

                checkoutExperienceExperiment={checkoutExperienceExperiment}
                linkCouponNotice={
                  autoCouponValidation.key === autoCouponValidationKey
                    ? autoCouponValidation.notice
                    : null
                }
              />
            </CheckoutStripeElements>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default PlanCheckoutPage;
